import React, { useEffect, useRef } from 'react';
import * as BABYLON from '@babylonjs/core';
import '@babylonjs/loaders/glTF';

const COCKPIT_IMG_URL =
  'https://customer-assets.emergentagent.com/job_intoxicated-drive/artifacts/ilf4p4yb_car_In.png';

const CRASH_AUDIO_URL =
  'https://customer-assets.emergentagent.com/job_intoxicated-drive/artifacts/ol304yya_u_mgq59j5ayf-sound-effect-car-crash-394903.mp3';

const VEHICLE_GLBS = [
  // BMW M3 E30 — flipped 180° so it faces forward (away from the player).
  { url: 'https://customer-assets.emergentagent.com/job_intoxicated-drive/artifacts/9q3mna1t_1992%20BMW%20M3%20%28E30%29.glb', category: 'suv', targetLen: 4.5, rotYExtra: Math.PI },
  // Toyota Corsa B box truck
  { url: 'https://customer-assets.emergentagent.com/job_intoxicated-drive/artifacts/6ozioh39_truck_toyota_corsa_b.glb', category: 'truck', targetLen: 6.0, rotYExtra: Math.PI },
  // Low-poly sedan — replaces the previous PSX-style Passat which had render
  // glitches (paper-flat white paint + inconsistent orientation on some
  // spawns). This model has proper geometry & textures.
  { url: 'https://customer-assets.emergentagent.com/job_intoxicated-drive/artifacts/vfda6sky_low_poly_car.glb', category: 'car', targetLen: 4.4, rotYExtra: Math.PI },
  // FSO Polonez MR93 — extra low-poly sedan variant for visual variety.
  { url: 'https://customer-assets.emergentagent.com/job_intoxicated-drive/artifacts/zva0fosd_1993_fso_polonez_mr93_lp.glb', category: 'car', targetLen: 4.4, rotYExtra: Math.PI }
];

const PALM_GLB_URL =
  'https://customer-assets.emergentagent.com/job_intoxicated-drive/artifacts/xokjaffb_palm_tree_55k_triangles.glb';

const STREETLIGHT_GLB_URL =
  'https://customer-assets.emergentagent.com/job_intoxicated-drive/artifacts/1e7rp3ns_caltrans_streetlight_2.glb';

const FENCE_GLB_URL =
  'https://customer-assets.emergentagent.com/job_intoxicated-drive/artifacts/2fqcpwlt_road_separation_fence.glb';

const NIGHT_SKY_URL =
  'https://customer-assets.emergentagent.com/job_intoxicated-drive/artifacts/ahj6ul56_night-sky.png';

const BANNER_IMAGE_URL =
  'https://customer-assets.emergentagent.com/job_intoxicated-drive/artifacts/cnul849u_Alcochek.png';

const BILLBOARD_GLB_URL =
  'https://customer-assets.emergentagent.com/job_intoxicated-drive/artifacts/fwhy0p6y_billboard.glb';

const GameScene = ({ drinkCount, onCrash, onUpdateStats, paused }) => {
  const canvasRef = useRef(null);
  const radioLabelRef = useRef(null);
  const sceneRef = useRef(null);
  const engineRef = useRef(null);
  const crashAudioRef = useRef(null);
  const pausedRef = useRef(false);

  useEffect(() => {
    pausedRef.current = !!paused;
  }, [paused]);

  // Preload crash sound effect (HTMLAudioElement — simple and reliable)
  useEffect(() => {
    const a = new Audio(CRASH_AUDIO_URL);
    a.preload = 'auto';
    a.volume = 0.85;
    crashAudioRef.current = a;
  }, []);
  const gameStateRef = useRef({
    startTime: Date.now(),
    distance: 0,
    speed: 0,
    crashed: false,
    inputBuffer: { left: 0, right: 0, up: 0, down: 0 },
    actualInputs: { left: false, right: false, up: false, down: false },
    carRotation: 0,
    carPosition: { x: 0, z: 0 },
    cameraSwayOffset: 0,
    traffic: [],
    roadSegments: [],
    laneMarkers: [],
    barriers: [],
    streetLights: [],
    flyoverPillars: [],
    flyoverGroundAnchored: [],
    steeringWheel: null,
    steeringInput: 0,
    wheelAngle: 0,         // smoothed steering wheel angle (radians)
    speedoNeedle: null,    // dashboard needle transform node
    audio: null,           // engine audio refs
    radio: {
      on: false,
      station: 0,          // 0=off, 1/2/3 = stations
      screen: null,        // mesh material refs for screen color
      screenLabel: null    // text on screen (HTML overlay)
    }
  });

  useEffect(() => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    // Detect a small/mobile viewport — used to trim rendering cost so the
    // scene stays fluid on phones (avoids the "environment stutter" where
    // road/props appear to jerk along with the car on low-FPS devices).
    const isMobileViewport =
      typeof window !== 'undefined' &&
      Math.min(window.innerWidth, window.innerHeight) < 500;
    // Engine options — kept minimal for cross-browser support. Safari/WebKit
    // rejects the `powerPreference` hint and older versions can bail on
    // unknown options, causing a blank canvas.
    const engineOptions = {
      preserveDrawingBuffer: true,
      stencil: true,
      // Fallback to WebGL1 if WebGL2 isn't available (older Safari on iOS 14)
      disableWebGL2Support: false,
      // Prevents "context lost" during background tab switches on some browsers
      doNotHandleContextLost: false,
      // Let Babylon respect devicePixelRatio when sizing the backing store —
      // otherwise the canvas renders at CSS pixels and looks blurry on
      // Retina/high-DPR phones.
      adaptToDeviceRatio: true
    };
    // Antialias ON everywhere — the pixelation on mobile was from an aggressive
    // hardware-scaling factor combined with AA-off, not from AA overhead.
    const engine = new BABYLON.Engine(canvas, true, engineOptions);
    // Hardware scaling factor: 1.0 = native resolution, >1 = render smaller
    // and upscale (blurrier, faster), <1 = supersample (sharper, slower).
    // On mobile we still need to keep some headroom for FPS but 1.8x was way
    // too soft on high-DPR iPhones. Clamp against DPR so DPR-3 devices don't
    // waste GPU on a 9× pixel budget while still keeping text/edges crisp.
    if (isMobileViewport) {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      // Target roughly ~1.5× CSS pixels of backing store on any DPR — that
      // stays sharp on Retina without blowing up fillrate.
      engine.setHardwareScalingLevel(Math.max(1.0, dpr / 1.5));
    } else {
      engine.setHardwareScalingLevel(1.15);
    }
    engineRef.current = engine;

    const scene = new BABYLON.Scene(engine);
    sceneRef.current = scene;
    scene.clearColor = new BABYLON.Color4(0.02, 0.02, 0.02, 1);
    // Performance: skip costly scene operations that aren't needed for this game
    scene.skipPointerMovePicking = true;
    scene.autoClearDepthAndStencil = true;
    scene.blockMaterialDirtyMechanism = true;
    scene.performancePriority = BABYLON.ScenePerformancePriority.Intermediate;

    // Cap simultaneous lights per material to 4 across the whole scene.
    // The scene has ~17 lights (streetlights + moon + hemi) which overflows
    // GL_MAX_VERTEX_UNIFORM_BUFFERS on Safari/WebKit and stricter WebGL
    // implementations (VERTEX shader uniform block count exceeds 14). Chrome
    // silently truncates, other browsers reject the shader → blank/broken game.
    // Hook every material's create so we cap it once and forget.
    scene.onNewMaterialAddedObservable.add((mat) => {
      if (mat && 'maxSimultaneousLights' in mat) {
        mat.maxSimultaneousLights = 4;
      }
    });

    // Camera setup - First person view
    const camera = new BABYLON.UniversalCamera('camera', new BABYLON.Vector3(0, 1.2, -2), scene);
    camera.setTarget(new BABYLON.Vector3(0, 1, 10));
    camera.fov = 1.2;

    // Lighting — much brighter ambient, blue-tinted moonlight tone
    const hemiLight = new BABYLON.HemisphericLight('hemiLight', new BABYLON.Vector3(0, 1, 0), scene);
    hemiLight.intensity = 0.7;
    hemiLight.diffuse = new BABYLON.Color3(0.65, 0.72, 0.88);
    hemiLight.groundColor = new BABYLON.Color3(0.15, 0.16, 0.22);

    // Soft directional moonlight for shadows and shape definition
    const moonLight = new BABYLON.DirectionalLight(
      'moonLight',
      new BABYLON.Vector3(-0.3, -1, 0.4),
      scene
    );
    moonLight.intensity = 0.7;
    moonLight.diffuse = new BABYLON.Color3(0.75, 0.8, 0.95);
    moonLight.specular = new BABYLON.Color3(0.5, 0.55, 0.7);

    // Create highway (recyclable segments for infinite road)
    createHighway(scene);

    // Create car (player)
    const car = createCar(scene);

    // Create traffic
    createTraffic(scene);

    // Create steering wheel POV
    createSteeringWheel(scene, camera);

    // Setup engine audio
    setupAudio(drinkCount);

    // Connect radio label DOM ref so setRadioStation can update text
    gameStateRef.current.radio.screenLabel = radioLabelRef.current;

    // Sync radio UI to its ON-by-default state once the audio + dashboard are built
    setRadioStation(1);

    // Setup impairment effects
    setupImpairmentEffects(scene, camera, drinkCount);

    // Input handling
    setupInputHandlers(drinkCount);

    // Game loop
    let lastTime = Date.now();
    scene.registerBeforeRender(() => {
      const now = Date.now();
      // Clamp deltaTime so a paused tab / slow frame can't blow up the physics
      // (which can cause lerps to overshoot and produce wild speed/distance values).
      const deltaTime = Math.min(0.05, (now - lastTime) / 1000);
      lastTime = now;

      if (gameStateRef.current.crashed) return;
      // While paused (loading / countdown), keep rendering but skip simulation.
      // This lets the scene fully warm up (texture decode, shader compile) before
      // the player has control — eliminating first-frame stutter.
      if (pausedRef.current) {
        // Reset the timer reference so distance/time don't jump when unpaused
        gameStateRef.current.startTime = Date.now();
        return;
      }

      updateCarPhysics(car, camera, deltaTime, drinkCount);
      updateInfiniteWorld();
      updateTraffic(scene, deltaTime);
      checkCollisions(car, scene);
      updateCameraEffects(camera, deltaTime, drinkCount);
      updateAudio(deltaTime);
      updateStats();
    });

    engine.runRenderLoop(() => {
      scene.render();
    });

    const handleResize = () => {
      engine.resize();
    };
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    // Also observe the canvas itself — on mobile the browser's address bar
    // can collapse/expand without firing a window resize, silently shrinking
    // the canvas and desyncing the WebGL viewport from the CSS box.
    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(handleResize)
      : null;
    if (resizeObserver) resizeObserver.observe(canvas);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      if (resizeObserver) resizeObserver.disconnect();
      const audio = gameStateRef.current.audio;
      if (audio) {
        try {
          if (audio.engineSource) audio.engineSource.stop();
        } catch (e) {
          // already stopped
        }
        audio.ctx.close();
      }
      scene.dispose();
      engine.dispose();
    };
  }, [drinkCount]);

  const SEGMENT_LENGTH = 8;
  const NUM_ROAD_SEGMENTS = 45;
  const MARKER_SPACING = 10;
  const NUM_MARKERS = 40;
  const BARRIER_SPACING = 4;
  const NUM_BARRIERS = 60;
  const LIGHT_SPACING = 28;
  const NUM_LIGHTS = 10;
  const EDGE_LINE_SPACING = 4;
  const NUM_EDGE_LINES = 90;
  const SCENERY_SPACING = 40;
  const NUM_SCENERY = 12;

  const createHighway = (scene) => {
    const state = gameStateRef.current;

    // Atmospheric fog for depth and night realism
    scene.fogMode = BABYLON.Scene.FOGMODE_EXP2;
    scene.fogColor = new BABYLON.Color3(0.04, 0.05, 0.08);
    scene.fogDensity = 0.005;

    // Shared materials — allow more simultaneous lights for smooth street-lamp coverage
    const ROAD_TEXTURE_URL =
      'https://customer-assets.emergentagent.com/job_intoxicated-drive/artifacts/y58gvt5s_road-texture.png';
    const BUILDING_TEXTURE_URL =
      'https://customer-assets.emergentagent.com/job_intoxicated-drive/artifacts/nt8i20s6_building.png';

    const roadMaterial = new BABYLON.StandardMaterial('roadMat', scene);
    const roadTex = new BABYLON.Texture(ROAD_TEXTURE_URL, scene);
    roadTex.uScale = 2;     // 2 horizontal tiles across the 20-unit-wide road
    roadTex.vScale = 0.64;  // ~1.6 tiles per 4-unit segment so visual tile stays ~6m
    roadTex.wrapU = BABYLON.Texture.WRAP_ADDRESSMODE;
    roadTex.wrapV = BABYLON.Texture.WRAP_ADDRESSMODE;
    roadMaterial.diffuseTexture = roadTex;
    roadMaterial.diffuseColor = new BABYLON.Color3(1, 1, 1); // let texture show
    roadMaterial.specularColor = new BABYLON.Color3(0.3, 0.3, 0.34);
    roadMaterial.specularPower = 32;
    roadMaterial.maxSimultaneousLights = 8;

    const shoulderMat = new BABYLON.StandardMaterial('shoulderMat', scene);
    shoulderMat.diffuseColor = new BABYLON.Color3(0.06, 0.06, 0.07);
    shoulderMat.maxSimultaneousLights = 8;

    const markerMat = new BABYLON.StandardMaterial('markerMat', scene);
    markerMat.emissiveColor = new BABYLON.Color3(1, 1, 0.85);
    markerMat.diffuseColor = new BABYLON.Color3(0.9, 0.9, 0.7);

    const edgeLineMat = new BABYLON.StandardMaterial('edgeLineMat', scene);
    edgeLineMat.diffuseColor = new BABYLON.Color3(0.85, 0.85, 0.85);
    edgeLineMat.emissiveColor = new BABYLON.Color3(0.3, 0.3, 0.3);

    const barrierMat = new BABYLON.StandardMaterial('barrierMat', scene);
    barrierMat.diffuseColor = new BABYLON.Color3(0.85, 0.1, 0.1);
    barrierMat.emissiveColor = new BABYLON.Color3(0.18, 0.02, 0.02);

    const poleMat = new BABYLON.StandardMaterial('poleMat', scene);
    poleMat.diffuseColor = new BABYLON.Color3(0.22, 0.22, 0.24);
    poleMat.specularColor = new BABYLON.Color3(0.4, 0.4, 0.4);

    const bulbMat = new BABYLON.StandardMaterial('bulbMat', scene);
    bulbMat.emissiveColor = new BABYLON.Color3(1, 0.92, 0.7);
    bulbMat.diffuseColor = new BABYLON.Color3(1, 0.9, 0.7);

    // Pooled road segments + shoulders. Each segment is drawn slightly LONGER
    // than the segment stride so adjacent segments overlap by ~15cm at both
    // ends, hiding the tilt seam between them (very visible under motion blur
    // + camera shake in the impaired states).
    const SEG_MESH_LEN = SEGMENT_LENGTH + 0.3;
    for (let i = 0; i < NUM_ROAD_SEGMENTS; i++) {
      const seg = BABYLON.MeshBuilder.CreateGround(
        `road${i}`,
        { width: 20, height: SEG_MESH_LEN },
        scene
      );
      seg.position.z = i * SEGMENT_LENGTH;
      seg.material = roadMaterial;
      state.roadSegments.push(seg);

      // Shoulders beside the road
      [-1, 1].forEach((side) => {
        const shoulder = BABYLON.MeshBuilder.CreateGround(
          `shoulder_${side}_${i}`,
          { width: 18, height: SEG_MESH_LEN },
          scene
        );
        shoulder.position = new BABYLON.Vector3(side * 18, -0.02, i * SEGMENT_LENGTH);
        shoulder.material = shoulderMat;
        state.roadSegments.push(shoulder);
      });
    }

    // Solid edge lines on both sides of the road
    for (let side of [-1, 1]) {
      for (let i = 0; i < NUM_EDGE_LINES; i++) {
        const edgeLine = BABYLON.MeshBuilder.CreateBox(
          `edge_${side}_${i}`,
          { width: 0.2, height: 0.04, depth: EDGE_LINE_SPACING * 0.95 },
          scene
        );
        edgeLine.position = new BABYLON.Vector3(side * 9.5, 0.03, i * EDGE_LINE_SPACING);
        edgeLine.material = edgeLineMat;
        state.laneMarkers.push(edgeLine);
      }
    }

    // Pooled lane markers (center dashes)
    for (let i = 0; i < NUM_MARKERS; i++) {
      const marker = BABYLON.MeshBuilder.CreateBox(
        `marker${i}`,
        { width: 0.3, height: 0.05, depth: 3 },
        scene
      );
      marker.position = new BABYLON.Vector3(0, 0.04, i * MARKER_SPACING);
      marker.material = markerMat;
      state.laneMarkers.push(marker);
    }

    // Pooled side barriers — use GLB fence model (async-loaded, cloned into placeholders)
    const fencePlaceholders = [];
    for (let side of [-1, 1]) {
      for (let i = 0; i < NUM_BARRIERS / 2; i++) {
        const barrierRoot = new BABYLON.TransformNode(`barrier_${side}_${i}`, scene);
        barrierRoot.position = new BABYLON.Vector3(side * 11, 0, i * BARRIER_SPACING);
        // Fence faces inward toward the road
        barrierRoot.rotation.y = side === 1 ? Math.PI : 0;
        fencePlaceholders.push({ node: barrierRoot, side });
        state.barriers.push({ mesh: barrierRoot, side });
      }
    }

    // Async load fence GLB, clone into placeholders. Auto-orient the model so
    // its longest dimension runs along Z (road direction) and shortest dimension
    // points across the road (X). The middle dimension is treated as height (Y).
    BABYLON.SceneLoader.ImportMeshAsync(null, '', FENCE_GLB_URL, scene)
      .then((result) => {
        const template = result.meshes[0];
        template.setEnabled(false);

        // Clamp light count on PBR materials so they don't blow the shader
        // when many SpotLights exist in the scene.
        result.meshes.forEach((m) => {
          if (m.material) m.material.maxSimultaneousLights = 4;
        });

        const bb = template.getHierarchyBoundingVectors(true);
        const dims = {
          x: bb.max.x - bb.min.x,
          y: bb.max.y - bb.min.y,
          z: bb.max.z - bb.min.z
        };
        const sorted = Object.entries(dims).sort((a, b) => a[1] - b[1]);
        const thinAxis = sorted[0][0];
        const tallAxis = sorted[1][0];
        const longAxis = sorted[2][0];

        // Rotation map: source-axis triple → Euler vector (applied via wrapper)
        // so that local long→world Z, local tall→world Y, local thin→world X.
        const rotMap = {
          'zyx': [0, 0, 0],                       // already aligned
          'xyz': [0, Math.PI / 2, 0],             // swap X↔Z
          'zxy': [0, 0, Math.PI / 2],             // tall on X → rotate around Z
          'xzy': [Math.PI / 2, Math.PI / 2, 0],   // Z-up + along X (Blender)
          'yzx': [Math.PI / 2, 0, 0],             // long Y, tall Z
          'yxz': [0, 0, -Math.PI / 2]             // long Y, tall X
        };
        const key = longAxis + tallAxis + thinAxis;
        const [rx, ry, rz] = rotMap[key] || [0, 0, 0];

        // Scale: match longest dim to BARRIER_SPACING (with slight overlap)
        const targetLength = BARRIER_SPACING * 1.02;
        const longSize = dims[longAxis];
        let scale = longSize > 0 ? targetLength / longSize : 1;
        // Clamp height to 1.6m max so the fence doesn't tower over the car
        const tallSize = dims[tallAxis] * scale;
        if (tallSize > 1.6) scale *= 1.6 / tallSize;

        fencePlaceholders.forEach((slot, idx) => {
          // Wrap the cloned instance in a node so we can apply scale+rotation
          // independently from the placeholder's positioning.
          const inst = template.clone(`fence_inst_${idx}`, null);
          inst.setEnabled(true);
          inst.scaling = new BABYLON.Vector3(scale, scale, scale);
          inst.rotation = new BABYLON.Vector3(rx, ry, rz);
          inst.parent = slot.node;
          // Drop the fence so its bottom sits on the road in the slot's local frame.
          // After scale + rotation, the local lowest point shifts; use world bb
          // and subtract slot world Y to convert back to local offset.
          inst.computeWorldMatrix(true);
          const wbb = inst.getHierarchyBoundingVectors(true);
          inst.position.y -= wbb.min.y - slot.node.position.y;
        });
      })
      .catch((err) => console.warn('Fence GLB load failed', err));

    // Background scenery — palm trees lining the highway (GLB model from Sketchfab)
    // Create empty placeholder TransformNodes first so recycling works immediately;
    // the actual palm GLB is async-loaded and cloned into each placeholder below.
    const palmPlaceholders = [];
    for (let side of [-1, 1]) {
      for (let i = 0; i < NUM_SCENERY / 2; i++) {
        const palm = new BABYLON.TransformNode(`palm_${side}_${i}`, scene);
        const baseX = side * (16 + Math.random() * 6);
        const baseZ = i * SCENERY_SPACING + Math.random() * 18;
        palm.position = new BABYLON.Vector3(baseX, 0, baseZ);
        palm.rotation.y = Math.random() * Math.PI * 2; // random Y rotation
        palmPlaceholders.push(palm);
        state.barriers.push({ mesh: palm, side, scenery: true });
      }
    }

    // Async load palm tree GLB, then clone for each placeholder
    BABYLON.SceneLoader.ImportMeshAsync(null, '', PALM_GLB_URL, scene)
      .then((result) => {
        const template = result.meshes[0];
        template.setEnabled(false);

        // Clamp light count on PBR materials so they don't blow the shader
        result.meshes.forEach((m) => {
          if (m.material) m.material.maxSimultaneousLights = 4;
        });

        // Measure template bounding box for auto-scaling to ~12m tall
        const bb = template.getHierarchyBoundingVectors(true);
        const measuredHeight = bb.max.y - bb.min.y;
        const targetHeight = 12;
        const baseScale = measuredHeight > 0 ? targetHeight / measuredHeight : 1;
        const minY = bb.min.y;

        palmPlaceholders.forEach((placeholder, idx) => {
          const palmInstance = template.clone(`palm_inst_${idx}`, null);
          palmInstance.setEnabled(true);
          // Random scale variation 0.85x — 1.2x for natural variety
          const variation = 0.85 + Math.random() * 0.35;
          const scale = baseScale * variation;
          palmInstance.scaling = new BABYLON.Vector3(scale, scale, scale);
          palmInstance.parent = placeholder;
          // Drop the model so its base sits on the ground
          palmInstance.position.y = -minY * scale;
        });
      })
      .catch((err) => console.warn('Palm GLB load failed', err));


    // Distant city skyline — textured buildings with randomized tint/brightness
    // The texture (windows + facade) is reused; each building gets its own material
    // so we can tint and modulate level for variety.
    const buildingTex = new BABYLON.Texture(BUILDING_TEXTURE_URL, scene);
    buildingTex.wrapU = BABYLON.Texture.WRAP_ADDRESSMODE;
    buildingTex.wrapV = BABYLON.Texture.WRAP_ADDRESSMODE;

    // Tint palette — cool blue, warm amber, neutral grey, teal, pink-purple
    const buildingTints = [
      new BABYLON.Color3(0.7, 0.78, 1.0),   // cool blue
      new BABYLON.Color3(1.0, 0.85, 0.62),  // warm amber
      new BABYLON.Color3(0.85, 0.85, 0.9),  // neutral grey-blue
      new BABYLON.Color3(0.6, 0.95, 0.85),  // teal
      new BABYLON.Color3(0.95, 0.7, 0.85),  // pink-purple
      new BABYLON.Color3(0.78, 0.82, 0.95)  // soft steel blue
    ];

    // 8 skyline groups that recycle as you drive — each contains 5-7 buildings
    for (let g = 0; g < 8; g++) {
      const group = new BABYLON.TransformNode(`skylineGroup_${g}`, scene);
      const groupZ = 80 + g * 90;
      group.position = new BABYLON.Vector3(0, 0, groupZ);

      for (let b = 0; b < 7; b++) {
        const height = 15 + Math.random() * 35;
        const width = 8 + Math.random() * 12;
        const depth = 8 + Math.random() * 8;
        const building = BABYLON.MeshBuilder.CreateBox(
          `bldg_${g}_${b}`,
          { width, height, depth },
          scene
        );
        // Always spawn buildings well off the road (road spans ±10; barriers at ±11)
        const buildingSide = Math.random() > 0.5 ? 1 : -1;
        const bldgX = buildingSide * (45 + Math.random() * 75);
        building.position = new BABYLON.Vector3(
          bldgX,
          height / 2,
          Math.random() * 40 - 20
        );

        // Per-building material: textured + tinted + randomized brightness/emissive
        const bldgMat = new BABYLON.StandardMaterial(`bldgMat_${g}_${b}`, scene);
        const tint = buildingTints[Math.floor(Math.random() * buildingTints.length)];
        const brightness = 0.55 + Math.random() * 0.6; // 0.55 — 1.15 multiplier
        bldgMat.diffuseColor = new BABYLON.Color3(
          tint.r * brightness,
          tint.g * brightness,
          tint.b * brightness
        );
        // Slight emissive so windows still feel lit at night even when not directly lit
        const emissiveAmt = 0.08 + Math.random() * 0.18;
        bldgMat.emissiveColor = new BABYLON.Color3(
          tint.r * emissiveAmt,
          tint.g * emissiveAmt,
          tint.b * emissiveAmt
        );
        bldgMat.specularColor = new BABYLON.Color3(0.05, 0.05, 0.05);

        // Each building gets its own texture clone so per-building UV scaling
        // matches the building dimensions (avoids stretching).
        const bldgTexClone = buildingTex.clone();
        // Scale UVs roughly proportional to dimensions (about 1 tile per 8 units)
        bldgTexClone.uScale = Math.max(1, Math.round(width / 8));
        bldgTexClone.vScale = Math.max(1, Math.round(height / 10));
        // Random U/V offset so neighboring buildings don't align identically
        bldgTexClone.uOffset = Math.random();
        bldgTexClone.vOffset = Math.random();
        bldgMat.diffuseTexture = bldgTexClone;
        bldgMat.emissiveTexture = bldgTexClone; // make lit windows glow

        building.material = bldgMat;
        building.parent = group;
      }
      // Recycle skyline groups via the barriers pool (scenery flag)
      state.barriers.push({ mesh: group, side: 0, scenery: true, skylineStride: 720 });
    }

    // ===== Far-distance skyline silhouettes =====
    // Tall dark building cutouts placed FAR from the road, filling the empty
    // horizon space behind the textured city blocks. These are deliberately
    // unlit and very dark so they read as silhouettes against the night sky.
    const silhouetteMat = new BABYLON.StandardMaterial('silhouetteMat', scene);
    silhouetteMat.diffuseColor = new BABYLON.Color3(0.04, 0.05, 0.08);
    silhouetteMat.emissiveColor = new BABYLON.Color3(0.02, 0.025, 0.05);
    silhouetteMat.specularColor = new BABYLON.Color3(0, 0, 0);
    silhouetteMat.disableLighting = true;

    // 6 silhouette groups recycle independently — each holds a row of 10 tall
    // narrow shapes positioned far behind the textured city blocks.
    for (let g = 0; g < 6; g++) {
      const silhouetteGroup = new BABYLON.TransformNode(`silhouetteGroup_${g}`, scene);
      const groupZ = 120 + g * 130;
      silhouetteGroup.position = new BABYLON.Vector3(0, 0, groupZ);

      for (let b = 0; b < 10; b++) {
        const height = 30 + Math.random() * 70; // varied skyline (30–100m)
        const width = 6 + Math.random() * 16;
        const depth = 6 + Math.random() * 8;
        const silhouette = BABYLON.MeshBuilder.CreateBox(
          `silhouette_${g}_${b}`,
          { width, height, depth },
          scene
        );
        const sSide = Math.random() > 0.5 ? 1 : -1;
        // Place much further out than textured buildings (which sit at ±45-120)
        const sX = sSide * (130 + Math.random() * 90);
        silhouette.position = new BABYLON.Vector3(
          sX,
          height / 2,
          Math.random() * 80 - 40
        );
        silhouette.material = silhouetteMat;
        silhouette.parent = silhouetteGroup;
      }
      state.barriers.push({ mesh: silhouetteGroup, side: 0, scenery: true, skylineStride: 780 });
    }

    // ===== Dynamic road features with DRIVABLE elevation =====
    // Flyovers raise the road; underpasses dip the road below an overhead crossing.
    // Spacing of ~600m + gentler/longer slopes = roughly one elevation event per ~15s
    // at typical driving speeds, with signs in between for variety.
    const featureStride = 125; // one feature every 125m → 1 banner + 1 billboard per 250m
    const featureCount = 14;

    const concreteMat = new BABYLON.StandardMaterial('concreteMat', scene);
    concreteMat.diffuseColor = new BABYLON.Color3(0.42, 0.42, 0.44);
    concreteMat.specularColor = new BABYLON.Color3(0.15, 0.15, 0.15);
    concreteMat.maxSimultaneousLights = 6;

    const concreteDarkMat = new BABYLON.StandardMaterial('concreteDarkMat', scene);
    concreteDarkMat.diffuseColor = new BABYLON.Color3(0.28, 0.28, 0.3);
    concreteDarkMat.specularColor = new BABYLON.Color3(0.1, 0.1, 0.1);
    concreteDarkMat.maxSimultaneousLights = 6;

    const signMat = new BABYLON.StandardMaterial('signMat', scene);
    signMat.diffuseColor = new BABYLON.Color3(0.15, 0.45, 0.18);
    signMat.emissiveColor = new BABYLON.Color3(0.08, 0.22, 0.1);
    signMat.specularColor = new BABYLON.Color3(0.1, 0.1, 0.1);

    const tunnelLightMat = new BABYLON.StandardMaterial('tunnelLightMat', scene);
    tunnelLightMat.emissiveColor = new BABYLON.Color3(1.0, 0.92, 0.7);
    tunnelLightMat.diffuseColor = new BABYLON.Color3(1.0, 0.9, 0.65);
    tunnelLightMat.disableLighting = true;

    // Pre-load shared textures used by signs and billboards
    const bannerTex = new BABYLON.Texture(BANNER_IMAGE_URL, scene);
    bannerTex.hasAlpha = false;

    // Feature mix: lots of banners + billboards every ~70m so player sees one
    // every ~10 seconds at cruise speed; occasional flyover/underpass for
    // road-elevation variety. Strides are 70m apart so 1 banner + 1 billboard
    // appear in every 140m (≈10s window).
    const featureKinds = [
      'banner', 'billboard', 'banner', 'billboard', 'banner', 'billboard',
      'flyover', 'banner', 'billboard', 'banner', 'billboard',
      'underpass', 'banner', 'billboard'
    ];

    // Billboard GLB: load ONCE, then clone into each 'billboard' feature slot.
    // Clones are parented to a hidden template until the GLB resolves.
    const billboardSlots = []; // { container, group }
    let billboardTemplate = null;
    let billboardTransform = { rx: 0, ry: 0, rz: 0, scale: 1, minY: 0 };

    BABYLON.SceneLoader.ImportMeshAsync(null, '', BILLBOARD_GLB_URL, scene)
      .then((result) => {
        const tpl = result.meshes[0];
        tpl.setEnabled(false);
        result.meshes.forEach((m) => {
          if (m.material) m.material.maxSimultaneousLights = 4;
        });
        // Auto-orient: tallest local axis becomes Y
        const bb = tpl.getHierarchyBoundingVectors(true);
        const dims = { x: bb.max.x - bb.min.x, y: bb.max.y - bb.min.y, z: bb.max.z - bb.min.z };
        const tallest = Object.entries(dims).sort((a, b) => b[1] - a[1])[0][0];
        let rx = 0, rz = 0;
        if (tallest === 'x') rz = -Math.PI / 2;
        else if (tallest === 'z') rx = Math.PI / 2;
        // Scale to ~9m tall (standard roadside billboard height)
        const targetHeight = 9;
        const scale = dims[tallest] > 0 ? targetHeight / dims[tallest] : 1;
        billboardTemplate = tpl;
        billboardTransform = { rx, ry: 0, rz, scale, minY: 0 };

        // Populate any pending slots
        billboardSlots.forEach((slot) => {
          const inst = tpl.clone(`billboard_inst_${slot.id}`, null);
          inst.setEnabled(true);
          inst.scaling = new BABYLON.Vector3(scale, scale, scale);
          inst.rotation = new BABYLON.Vector3(rx, 0, rz);
          inst.parent = slot.container;
          inst.computeWorldMatrix(true);
          const wbb = inst.getHierarchyBoundingVectors(true);
          inst.position.y -= wbb.min.y - slot.container.position.y;
        });
      })
      .catch((err) => console.warn('Billboard GLB load failed', err));

    for (let f = 0; f < featureCount; f++) {
      const baseZ = 220 + f * featureStride;
      const kind = featureKinds[f % featureKinds.length];
      const featureGroup = new BABYLON.TransformNode(`feature_${f}`, scene);
      featureGroup.position = new BABYLON.Vector3(0, 0, baseZ);

      let elevationEvent = null;

      if (kind === 'flyover') {
        // Tall enough that vehicles can drive UNDER an overpass with clearance.
        // 10–12m peak gives a comfortable ~7m clearance over the lower road.
        const amplitude = 10 + Math.random() * 2;
        const length = 280 + Math.random() * 60;
        elevationEvent = { amplitude, length };

        // Concrete support pillars flanking the road along the elevated section.
        // Tall enough that the lower half stays rooted below ground even when
        // the road is at its peak; the runtime loop in updateInfiniteWorld sets
        // each pillar's Y each frame so its TOP meets the road surface.
        const pillarHeight = amplitude * 3 + 6;
        [-1, 1].forEach((side) => {
          [-2, -1, 0, 1, 2].forEach((zSide) => {
            const pillar = BABYLON.MeshBuilder.CreateCylinder(
              `pillar_${f}_${side}_${zSide}`,
              { height: pillarHeight, diameter: 1.4, tessellation: 12 },
              scene
            );
            const zOffset = zSide * length * 0.18;
            pillar.position = new BABYLON.Vector3(side * 12, 0, zOffset);
            pillar.material = concreteMat;
            pillar.parent = featureGroup;
            state.flyoverPillars.push({
              mesh: pillar,
              group: featureGroup,
              zOffset,
              height: pillarHeight
            });
          });
        });

        // Water / sea body filling the area BESIDE the elevated road.
        // Bright enough to read against the dark sky; positioned close to the
        // road so the elevated player clearly sees it filling the field of view.
        const seaMat = new BABYLON.StandardMaterial(`seaMat_${f}`, scene);
        seaMat.diffuseColor = new BABYLON.Color3(0.08, 0.18, 0.34);
        seaMat.emissiveColor = new BABYLON.Color3(0.18, 0.28, 0.48);
        seaMat.specularColor = new BABYLON.Color3(0.7, 0.85, 1.0);
        seaMat.specularPower = 32;

        [-1, 1].forEach((side) => {
          const water = BABYLON.MeshBuilder.CreateGround(
            `flyover_sea_${f}_${side}`,
            { width: 260, height: length * 1.6, subdivisions: 1 },
            scene
          );
          water.position = new BABYLON.Vector3(side * 145, -1.5, 0);
          water.material = seaMat;
          water.parent = featureGroup;
          state.flyoverGroundAnchored.push({ mesh: water, group: featureGroup, absY: -1.5 });
        });

        // Bright moonlight shimmer streaks running along the water surface
        const shimmerMat = new BABYLON.StandardMaterial(`shimmerMat_${f}`, scene);
        shimmerMat.emissiveColor = new BABYLON.Color3(0.92, 0.95, 1.0);
        shimmerMat.diffuseColor = new BABYLON.Color3(0, 0, 0);
        shimmerMat.disableLighting = true;
        shimmerMat.alpha = 0.9;

        [-1, 1].forEach((side) => {
          for (let s = 0; s < 18; s++) {
            const streak = BABYLON.MeshBuilder.CreateBox(
              `shimmer_${f}_${side}_${s}`,
              { width: 22 + Math.random() * 40, height: 0.06, depth: 0.6 + Math.random() * 0.7 },
              scene
            );
            streak.position = new BABYLON.Vector3(
              side * (25 + Math.random() * 230),
              -1.4,
              (Math.random() - 0.5) * length * 1.5
            );
            streak.material = shimmerMat;
            streak.parent = featureGroup;
            state.flyoverGroundAnchored.push({ mesh: streak, group: featureGroup, absY: -1.4 });
          }
        });
      } else if (kind === 'underpass') {
        // Gentle dip — 3-4.5m over 250-300m for a realistic underpass approach
        const amplitude = -(3 + Math.random() * 1.5);
        const length = 240 + Math.random() * 60;
        elevationEvent = { amplitude, length };

        // Overhead crossing bridge perpendicular to highway (sits at y=0)
        const deck = BABYLON.MeshBuilder.CreateBox(
          `underpassDeck_${f}`,
          { width: 80, height: 1.2, depth: 10 },
          scene
        );
        deck.position = new BABYLON.Vector3(0, 0.5, 0);
        deck.material = concreteMat;
        deck.parent = featureGroup;

        // Parapets along the crossing bridge
        [-1, 1].forEach((side) => {
          const parapet = BABYLON.MeshBuilder.CreateBox(
            `underpassParapet_${f}_${side}`,
            { width: 80, height: 1.2, depth: 0.6 },
            scene
          );
          parapet.position = new BABYLON.Vector3(0, 1.55, side * 4.7);
          parapet.material = concreteDarkMat;
          parapet.parent = featureGroup;
        });

        // Tall support columns flanking the road (anchored from ground level)
        [-1, 1].forEach((side) => {
          [-1, 1].forEach((zSide) => {
            const col = BABYLON.MeshBuilder.CreateCylinder(
              `underpassCol_${f}_${side}_${zSide}`,
              { height: 6, diameter: 1.6, tessellation: 12 },
              scene
            );
            col.position = new BABYLON.Vector3(side * 13, -2, zSide * 4);
            col.material = concreteMat;
            col.parent = featureGroup;
          });
        });

        // Lights mounted on the underside of the bridge so the dipped road is lit
        for (let l = 0; l < 3; l++) {
          const bulb = BABYLON.MeshBuilder.CreateBox(
            `underpassBulb_${f}_${l}`,
            { width: 1.5, height: 0.2, depth: 0.4 },
            scene
          );
          bulb.position = new BABYLON.Vector3(0, -0.2, (l - 1) * 3);
          bulb.material = tunnelLightMat;
          bulb.parent = featureGroup;

          const pl = new BABYLON.PointLight(
            `underpassLight_${f}_${l}`,
            new BABYLON.Vector3(0, -0.2, (l - 1) * 3),
            scene
          );
          pl.intensity = 1.4;
          pl.diffuse = new BABYLON.Color3(1, 0.9, 0.7);
          pl.range = 16;
          pl.parent = featureGroup;
          // Disable — see streetSpot comment. Total scene lights must stay ≤ 4
          // to render on Safari/WebKit. The bulb mesh above still glows.
          pl.setEnabled(false);
        }
      } else if (kind === 'banner') {
        // Overhead highway gantry banner — green panel framed by two posts and a
        // beam. The Alcochek graphic is mapped DIRECTLY onto the panel front
        // (replacing the green fill).
        const gantryHeight = 6.5;
        const beam = BABYLON.MeshBuilder.CreateBox(
          `signBeam_${f}`,
          { width: 26, height: 0.45, depth: 0.45 },
          scene
        );
        beam.position = new BABYLON.Vector3(0, gantryHeight, 0);
        beam.material = concreteDarkMat;
        beam.parent = featureGroup;

        [-1, 1].forEach((side) => {
          const post = BABYLON.MeshBuilder.CreateCylinder(
            `signPost_${f}_${side}`,
            { height: gantryHeight, diameter: 0.5, tessellation: 12 },
            scene
          );
          post.position = new BABYLON.Vector3(side * 12.5, gantryHeight / 2, 0);
          post.material = concreteDarkMat;
          post.parent = featureGroup;
        });

        // Panel with the Alcochek image baked onto its front face. Texture
        // wraps all 6 box faces but only the front one is visible to the
        // oncoming driver. Per-feature material so it always overrides signMat.
        const panel = BABYLON.MeshBuilder.CreateBox(
          `signPanel_${f}`,
          { width: 14, height: 3, depth: 0.2 },
          scene
        );
        panel.position = new BABYLON.Vector3(0, gantryHeight - 1.7, 0);
        const panelMat = new BABYLON.StandardMaterial(`bannerPanelMat_${f}`, scene);
        panelMat.diffuseTexture = bannerTex;
        panelMat.emissiveTexture = bannerTex;
        panelMat.emissiveColor = new BABYLON.Color3(0.9, 0.9, 0.9);
        panelMat.specularColor = new BABYLON.Color3(0, 0, 0);
        panelMat.disableLighting = true;
        panel.material = panelMat;
        panel.parent = featureGroup;
      } else if (kind === 'billboard') {
        // Roadside billboard — GLB frame only. Random side per instance.
        const billboardSide = Math.random() > 0.5 ? -1 : 1;
        const billboardContainer = new BABYLON.TransformNode(`billboardC_${f}`, scene);
        billboardContainer.position = new BABYLON.Vector3(billboardSide * 18, 0, 0);
        billboardContainer.rotation.y = billboardSide === 1 ? Math.PI / 2 : -Math.PI / 2;
        billboardContainer.parent = featureGroup;

        if (billboardTemplate) {
          const { rx, rz, scale } = billboardTransform;
          const inst = billboardTemplate.clone(`billboard_inst_${f}`, null);
          inst.setEnabled(true);
          inst.scaling = new BABYLON.Vector3(scale, scale, scale);
          inst.rotation = new BABYLON.Vector3(rx, 0, rz);
          inst.parent = billboardContainer;
          inst.computeWorldMatrix(true);
          const wbb = inst.getHierarchyBoundingVectors(true);
          inst.position.y -= wbb.min.y - billboardContainer.position.y;
        } else {
          billboardSlots.push({ id: f, container: billboardContainer });
        }
      }

      state.barriers.push({
        mesh: featureGroup,
        side: 0,
        scenery: true,
        skylineStride: featureStride * featureCount,
        elevationEvent
      });
    }

    // Night sky — inverted cylinder around the player with the user-uploaded
    // night-sky.png mapped to the inside. Wider tessellation and a tall height
    // ensures the player sees the texture in all forward directions.
    const sky = BABYLON.MeshBuilder.CreateCylinder(
      'sky',
      {
        height: 600,
        diameter: 900,
        tessellation: 48,
        sideOrientation: BABYLON.Mesh.BACKSIDE,
        cap: BABYLON.Mesh.NO_CAP
      },
      scene
    );
    sky.position.y = 250; // raise so the bottom of the cylinder hides below horizon
    sky.infiniteDistance = true;

    const skyTex = new BABYLON.Texture(NIGHT_SKY_URL, scene);
    skyTex.wrapU = BABYLON.Texture.CLAMP_ADDRESSMODE;
    skyTex.wrapV = BABYLON.Texture.CLAMP_ADDRESSMODE;

    const skyMat = new BABYLON.StandardMaterial('skyMat', scene);
    skyMat.emissiveTexture = skyTex;
    skyMat.emissiveColor = new BABYLON.Color3(1, 1, 1);
    skyMat.diffuseColor = new BABYLON.Color3(0, 0, 0);
    skyMat.specularColor = new BABYLON.Color3(0, 0, 0);
    skyMat.backFaceCulling = false;
    skyMat.disableLighting = true;
    sky.material = skyMat;

    // Sky CAP — a flat ceiling above the cylinder so the player never sees a
    // black hole when looking straight up. Uses the SAME texture, sampled near
    // the top of the image (the darker upper portion of the night sky).
    const skyCap = BABYLON.MeshBuilder.CreateDisc(
      'skyCap',
      { radius: 450, tessellation: 48, sideOrientation: BABYLON.Mesh.BACKSIDE },
      scene
    );
    skyCap.rotation.x = Math.PI / 2; // lay flat
    skyCap.position.y = 550;
    skyCap.infiniteDistance = true;
    const capMat = new BABYLON.StandardMaterial('skyCapMat', scene);
    capMat.emissiveColor = new BABYLON.Color3(0.04, 0.06, 0.12);
    capMat.diffuseColor = new BABYLON.Color3(0, 0, 0);
    capMat.specularColor = new BABYLON.Color3(0, 0, 0);
    capMat.disableLighting = true;
    skyCap.material = capMat;

    // Pooled street lights — GLB pole/arm/housing + spot light for road illumination.
    // We DELAY creating the bulb/spot positions because we don't know where the
    // GLB's lamp head ends up until it's loaded. Placeholder TransformNodes are
    // created now so recycling logic works synchronously.
    const streetlightPlaceholders = [];
    for (let side of [-1, 1]) {
      for (let i = 0; i < NUM_LIGHTS / 2; i++) {
        // Pole root (the GLB will be parented inside this).
        const poleRoot = new BABYLON.TransformNode(`pole_${side}_${i}`, scene);
        poleRoot.position = new BABYLON.Vector3(side * 13, 0, i * LIGHT_SPACING);
        // Rotation is set AFTER GLB loads (we need to know which way the arm
        // points in the model to flip per side so it always faces the road).
        streetlightPlaceholders.push({ node: poleRoot, side, slotIndex: state.streetLights.length });

        // Glowing bulb sphere — initial position will be corrected once the
        // GLB is loaded and we know where the lamp head is in world space.
        const bulb = BABYLON.MeshBuilder.CreateSphere(
          `bulb_${side}_${i}`,
          { diameter: 0.7 },
          scene
        );
        bulb.position = new BABYLON.Vector3(side * 10, 8.4, i * LIGHT_SPACING);
        bulb.material = bulbMat;
        bulb.setEnabled(false); // hidden until GLB loads and we can align it

        // Strong downward-cone spot light (ambient illumination of road).
        // Disabled by default — the game already has enough total lights that
        // enabling every streetlight overflows GL_MAX_VERTEX_UNIFORM_BUFFERS
        // on Safari/WebKit (17 lights > cap of 14 → blank canvas). We keep
        // the SpotLight *object* so the visual bulb glow is still colored
        // consistently, but the light itself is off for cross-browser safety.
        const spot = new BABYLON.SpotLight(
          `streetSpot_${side}_${i}`,
          new BABYLON.Vector3(side * 10, 8.4, i * LIGHT_SPACING),
          new BABYLON.Vector3(0, -1, 0),
          Math.PI / 2.2,
          1.5,
          scene
        );
        spot.intensity = 2.2;
        spot.diffuse = new BABYLON.Color3(1, 0.92, 0.72);
        spot.specular = new BABYLON.Color3(1, 0.92, 0.72);
        spot.range = 28;
        spot.setEnabled(false);

        // lampOffsetX/Y store the lamp head offset from pole base (filled when GLB loads)
        state.streetLights.push({
          pole: poleRoot,
          bulb,
          light: spot,
          arm: null,
          housing: null,
          side,
          lampOffsetX: side * -3, // tentative inward offset
          lampOffsetY: 8.4
        });
      }
    }

    // Async load streetlight GLB, clone into placeholders. Auto-orient so the
    // tallest axis stands up (world Y), then detect which horizontal direction
    // the lamp ARM extends in. Flip each slot so the arm always points INWARD
    // toward the road center (x=0). Move bulb + spot to the actual lamp head.
    BABYLON.SceneLoader.ImportMeshAsync(null, '', STREETLIGHT_GLB_URL, scene)
      .then((result) => {
        const template = result.meshes[0];
        template.setEnabled(false);

        result.meshes.forEach((m) => {
          if (m.material) m.material.maxSimultaneousLights = 4;
        });

        const bb = template.getHierarchyBoundingVectors(true);
        const dims = {
          x: bb.max.x - bb.min.x,
          y: bb.max.y - bb.min.y,
          z: bb.max.z - bb.min.z
        };
        const tallestAxis = Object.entries(dims).sort((a, b) => b[1] - a[1])[0][0];
        let rx = 0, ry = 0, rz = 0;
        if (tallestAxis === 'x') rz = -Math.PI / 2;
        else if (tallestAxis === 'z') rx = Math.PI / 2;

        const targetHeight = 9;
        const measuredHeight = dims[tallestAxis];
        const scale = measuredHeight > 0 ? targetHeight / measuredHeight : 1;

        // Probe clone: apply rotation+scale, measure post-transform bbox to
        // find where the lamp head sits (highest point's horizontal offset).
        const probe = template.clone('streetlight_probe', null);
        probe.setEnabled(false);
        probe.scaling = new BABYLON.Vector3(scale, scale, scale);
        probe.rotation = new BABYLON.Vector3(rx, ry, rz);
        probe.computeWorldMatrix(true);
        const pbb = probe.getHierarchyBoundingVectors(true);
        // Lamp head world Y (top of model)
        const lampWorldY = pbb.max.y;
        // The arm extends to whichever side has greater absolute X extent.
        // Mid-X tells us the dominant arm direction.
        const midX = (pbb.max.x + pbb.min.x) / 2;
        // armDir is the natural arm direction from the GLB: +1 means arm points +x.
        const armDir = midX >= 0 ? 1 : -1;
        // Horizontal distance from pole base to lamp head
        const armReach = Math.max(Math.abs(pbb.max.x), Math.abs(pbb.min.x));
        probe.dispose();

        streetlightPlaceholders.forEach((slot) => {
          const inst = template.clone(`streetlight_inst_${slot.slotIndex}`, null);
          inst.setEnabled(true);
          inst.scaling = new BABYLON.Vector3(scale, scale, scale);
          inst.rotation = new BABYLON.Vector3(rx, ry, rz);
          inst.parent = slot.node;
          // Drop so base sits on ground
          inst.computeWorldMatrix(true);
          const wbb = inst.getHierarchyBoundingVectors(true);
          inst.position.y -= wbb.min.y - slot.node.position.y;

          // Orient the pole so the arm points INWARD toward the road center.
          // For pole at x=side*13, we want arm extending in direction (-side).
          // If GLB's natural armDir matches (-side), no flip; otherwise flip 180°.
          const desiredDir = slot.side;
          const needFlip = desiredDir !== armDir;
          slot.node.rotation.y = needFlip ? Math.PI : 0;

          // Now position the bulb + spot at the actual lamp head world coords.
          // After the Y-rotation flip, the arm's world direction inverts.
          const actualArmDir = needFlip ? -armDir : armDir;
          const entry = state.streetLights[slot.slotIndex];
          const finalArmWorldX = slot.node.position.x + actualArmDir * armReach;
          // Store offset from pole-base so the recycling code can keep them
          // synced as the pole moves along Z.
          entry.lampOffsetX = finalArmWorldX - slot.node.position.x;
          entry.lampOffsetY = lampWorldY;
          entry.bulb.position.x = finalArmWorldX;
          entry.bulb.position.y = lampWorldY;
          entry.bulb.setEnabled(false); // bulb removed — lamp head from GLB is sufficient
          entry.light.position.x = finalArmWorldX;
          entry.light.position.y = lampWorldY;
        });
      })
      .catch((err) => console.warn('Streetlight GLB load failed', err));
  };

  // Recycle road, markers, barriers, street lights forward as the player advances
  const updateInfiniteWorld = () => {
    const state = gameStateRef.current;
    const playerZ = state.carPosition.z;

    const recycle = (mesh, spacing, count, offsetAhead) => {
      const totalLength = spacing * count;
      // Place each element at the nearest position ahead of player
      const baseZ = mesh.position.z;
      while (baseZ - mesh.position.z + (mesh.position.z - playerZ) < -offsetAhead) {
        mesh.position.z += totalLength;
      }
      while (mesh.position.z - playerZ > totalLength - offsetAhead) {
        mesh.position.z -= totalLength;
      }
    };

    // Road segments
    const roadTotal = SEGMENT_LENGTH * NUM_ROAD_SEGMENTS;
    state.roadSegments.forEach((seg) => {
      while (seg.position.z < playerZ - SEGMENT_LENGTH * 1.5) {
        seg.position.z += roadTotal;
      }
      while (seg.position.z > playerZ + SEGMENT_LENGTH * (NUM_ROAD_SEGMENTS - 1)) {
        seg.position.z -= roadTotal;
      }
    });

    // Lane markers (center dashes — most are at MARKER_SPACING; edge lines at EDGE_LINE_SPACING.
    // Recycle each marker by its individual stride so center and edge lines wrap cleanly.)
    const markerTotal = MARKER_SPACING * NUM_MARKERS;
    const edgeTotal = EDGE_LINE_SPACING * NUM_EDGE_LINES;
    state.laneMarkers.forEach((marker) => {
      const isEdge = marker.name.startsWith('edge_');
      const total = isEdge ? edgeTotal : markerTotal;
      const spacing = isEdge ? EDGE_LINE_SPACING : MARKER_SPACING;
      while (marker.position.z < playerZ - spacing * 1.5) {
        marker.position.z += total;
      }
      while (marker.position.z > playerZ + total - spacing * 1.5) {
        marker.position.z -= total;
      }
    });

    // Barriers + scenery (palms) + skyline share the barriers pool with stride flags.
    const barrierTotal = BARRIER_SPACING * (NUM_BARRIERS / 2);
    const sceneryTotal = SCENERY_SPACING * (NUM_SCENERY / 2);
    state.barriers.forEach((entry) => {
      const mesh = entry.mesh;
      const isScenery = entry.scenery === true;
      const isSkyline = typeof entry.skylineStride === 'number';
      const hasEvent = !!entry.elevationEvent;
      const total = isSkyline
        ? entry.skylineStride
        : isScenery
        ? sceneryTotal
        : barrierTotal;
      // For elevation events, wait until the player is FAR past the event's
      // influence zone before recycling. Otherwise the bump suddenly disappears
      // mid-curve and the car/road glitches.
      const spacing = isSkyline ? 90 : isScenery ? SCENERY_SPACING : BARRIER_SPACING;
      const recycleBuffer = hasEvent
        ? entry.elevationEvent.length * 1.6
        : spacing * 2;
      while (mesh.position.z < playerZ - recycleBuffer) {
        mesh.position.z += total;
      }
      while (mesh.position.z > playerZ + total - recycleBuffer) {
        mesh.position.z -= total;
      }
    });

    // Street lights (recycle pole, bulb, spot light, arm, housing together)
    const lightTotal = LIGHT_SPACING * (NUM_LIGHTS / 2);
    state.streetLights.forEach(({ pole, bulb, light, arm, housing }) => {
      while (pole.position.z < playerZ - LIGHT_SPACING) {
        pole.position.z += lightTotal;
        bulb.position.z += lightTotal;
        light.position.z += lightTotal;
        if (arm) arm.position.z += lightTotal;
        if (housing) housing.position.z += lightTotal;
      }
      while (pole.position.z > playerZ + lightTotal - LIGHT_SPACING) {
        pole.position.z -= lightTotal;
        bulb.position.z -= lightTotal;
        light.position.z -= lightTotal;
        if (arm) arm.position.z -= lightTotal;
        if (housing) housing.position.z -= lightTotal;
      }
    });

    // ===== Apply road elevation to every surface-following mesh =====
    // Road segments and edge lines also TILT to match the local elevation gradient,
    // so adjacent short segments form a smooth ramp rather than discrete steps.
    const tiltFromZ = (z) => {
      const dy = getRoadY(z + 1) - getRoadY(z - 1);
      return -Math.atan2(dy, 2);
    };

    state.roadSegments.forEach((seg) => {
      seg.position.y = getRoadY(seg.position.z);
      seg.rotation.x = tiltFromZ(seg.position.z);
    });
    state.laneMarkers.forEach((marker) => {
      const isEdge = marker.name.startsWith('edge_');
      marker.position.y = getRoadY(marker.position.z) + (isEdge ? 0.03 : 0.04);
      if (isEdge) {
        marker.rotation.x = tiltFromZ(marker.position.z);
      }
    });
    state.barriers.forEach((entry) => {
      const name = entry.mesh.name || '';
      if (name.startsWith('skylineGroup_') || name.startsWith('silhouetteGroup_')) return;
      // Flyover/underpass groups stay at y=0 (their pillars + water track ground
      // independently). Banner/billboard groups DO snap to roadY so their
      // gantry+panel rise with the elevated road instead of being buried in it.
      if (entry.elevationEvent) return;
      if (name.startsWith('feature_')) {
        // Banner / billboard feature groups
        entry.mesh.position.y = getRoadY(entry.mesh.position.z);
        return;
      }
      // Fences, palms, and any other ground-following objects.
      entry.mesh.position.y = getRoadY(entry.mesh.position.z);
    });

    // Flyover pillars: parented to feature group (which sits at y=0). Set each
    // pillar's Y so its TOP reaches the road surface at its world Z.
    state.flyoverPillars.forEach((p) => {
      const worldZ = p.group.position.z + p.zOffset;
      const roadY = getRoadY(worldZ);
      p.mesh.position.y = roadY - p.height / 2;
    });
    state.streetLights.forEach((entry) => {
      const { pole, bulb, light } = entry;
      const roadY = getRoadY(pole.position.z);
      pole.position.y = roadY; // GLB root sits on road
      // Bulb + spot sit at the GLB-derived lamp head offset (set when GLB loaded).
      // lampOffsetX is the inward offset from the pole base in world X.
      bulb.position.x = pole.position.x + entry.lampOffsetX;
      bulb.position.y = entry.lampOffsetY + roadY;
      light.position.x = pole.position.x + entry.lampOffsetX;
      light.position.y = entry.lampOffsetY + roadY;
    });

    // Hide skyline buildings (textured + silhouette) when they overlap a flyover
    // zone so the water/sea effect dominates the view during elevated sections.
    const flyovers = state.barriers.filter(
      (e) => e.elevationEvent && e.elevationEvent.amplitude > 0
    );
    state.barriers.forEach((entry) => {
      if (typeof entry.skylineStride !== 'number') return; // only skyline groups
      if (entry.elevationEvent) return; // skip feature groups themselves (flyover/underpass/sign)
      // Identify city blocks + silhouettes by mesh name; skip everything else.
      const name = entry.mesh.name || '';
      if (!name.startsWith('skylineGroup_') && !name.startsWith('silhouetteGroup_')) return;

      let inFlyover = false;
      for (const fov of flyovers) {
        const dz = Math.abs(entry.mesh.position.z - fov.mesh.position.z);
        // Generous detection window so buildings hide BEFORE the road visually
        // elevates and stay hidden through the whole arc.
        if (dz < fov.elevationEvent.length * 1.1) {
          inFlyover = true;
          break;
        }
      }
      if (entry.mesh.isEnabled() === inFlyover) {
        entry.mesh.setEnabled(!inFlyover);
      }
    });
  };

  // getRoadY: returns the elevation at any Z. Sum of Gaussian bumps from each
  // active feature's elevationEvent. Players ride this curve up/down smoothly.
  const getRoadY = (z) => {
    let y = 0;
    const state = gameStateRef.current;
    for (const entry of state.barriers) {
      const ev = entry.elevationEvent;
      if (!ev) continue;
      const dz = z - entry.mesh.position.z;
      const halfLen = ev.length / 2;
      if (Math.abs(dz) > halfLen * 2.2) continue;
      const normalized = dz / halfLen;
      // Smooth Gaussian: amplitude × exp(-x² × 1.2)
      y += ev.amplitude * Math.exp(-normalized * normalized * 1.2);
    }
    return y;
  };

  const buildCarMesh = (scene, name, color, isPlayer = false) => {
    // Parent transform node
    const root = new BABYLON.TransformNode(`${name}_root`, scene);

    // Shared materials
    const bodyMat = new BABYLON.StandardMaterial(`${name}_bodyMat`, scene);
    bodyMat.diffuseColor = color;
    bodyMat.specularColor = new BABYLON.Color3(0.9, 0.9, 0.9);
    bodyMat.specularPower = 128;
    bodyMat.emissiveColor = color.scale(0.05);

    const trimMat = new BABYLON.StandardMaterial(`${name}_trimMat`, scene);
    trimMat.diffuseColor = new BABYLON.Color3(0.04, 0.04, 0.05);
    trimMat.specularColor = new BABYLON.Color3(0.6, 0.6, 0.6);

    const glassMat = new BABYLON.StandardMaterial(`${name}_glassMat`, scene);
    glassMat.diffuseColor = new BABYLON.Color3(0.04, 0.06, 0.12);
    glassMat.specularColor = new BABYLON.Color3(1, 1, 1);
    glassMat.specularPower = 256;
    glassMat.alpha = 0.55;

    const chromeMat = new BABYLON.StandardMaterial(`${name}_chromeMat`, scene);
    chromeMat.diffuseColor = new BABYLON.Color3(0.55, 0.55, 0.6);
    chromeMat.specularColor = new BABYLON.Color3(1, 1, 1);
    chromeMat.specularPower = 256;
    chromeMat.emissiveColor = new BABYLON.Color3(0.05, 0.05, 0.06);

    // Lower chassis (wider, flatter)
    const chassis = BABYLON.MeshBuilder.CreateBox(
      `${name}_chassis`,
      { width: 1.9, height: 0.35, depth: 4.4 },
      scene
    );
    chassis.position.y = 0.28;
    chassis.parent = root;
    chassis.material = bodyMat;

    // Hood (sloped front)
    const hood = BABYLON.MeshBuilder.CreateBox(
      `${name}_hood`,
      { width: 1.85, height: 0.35, depth: 1.4 },
      scene
    );
    hood.position = new BABYLON.Vector3(0, 0.6, 1.1);
    hood.rotation.x = -0.05;
    hood.parent = root;
    hood.material = bodyMat;

    // Trunk/rear deck
    const trunk = BABYLON.MeshBuilder.CreateBox(
      `${name}_trunk`,
      { width: 1.85, height: 0.35, depth: 1.2 },
      scene
    );
    trunk.position = new BABYLON.Vector3(0, 0.6, -1.3);
    trunk.parent = root;
    trunk.material = bodyMat;

    // Mid body where doors are (slightly wider at the bottom)
    const midBody = BABYLON.MeshBuilder.CreateBox(
      `${name}_midBody`,
      { width: 1.9, height: 0.55, depth: 2.1 },
      scene
    );
    midBody.position = new BABYLON.Vector3(0, 0.7, -0.1);
    midBody.parent = root;
    midBody.material = bodyMat;

    // Cabin/greenhouse (narrower, with curved feel via scaling)
    const cabin = BABYLON.MeshBuilder.CreateBox(
      `${name}_cabin`,
      { width: 1.65, height: 0.65, depth: 1.9 },
      scene
    );
    cabin.position = new BABYLON.Vector3(0, 1.18, -0.15);
    cabin.parent = root;
    const cabinMat = new BABYLON.StandardMaterial(`${name}_cabinMat`, scene);
    cabinMat.diffuseColor = color.scale(0.55);
    cabinMat.specularColor = new BABYLON.Color3(0.7, 0.7, 0.7);
    cabinMat.specularPower = 96;
    cabin.material = cabinMat;

    // A-pillars (front roof posts)
    [-1, 1].forEach((side) => {
      const aPillar = BABYLON.MeshBuilder.CreateBox(
        `${name}_aPillar_${side}`,
        { width: 0.1, height: 0.55, depth: 0.1 },
        scene
      );
      aPillar.position = new BABYLON.Vector3(side * 0.82, 1.1, 0.75);
      aPillar.rotation.x = -0.3;
      aPillar.parent = root;
      aPillar.material = trimMat;
    });

    // Windshield (front glass, angled)
    const windshield = BABYLON.MeshBuilder.CreateBox(
      `${name}_ws`,
      { width: 1.55, height: 0.65, depth: 0.04 },
      scene
    );
    windshield.position = new BABYLON.Vector3(0, 1.18, 0.78);
    windshield.rotation.x = -0.4;
    windshield.parent = root;
    windshield.material = glassMat;

    // Rear window
    const rearWindow = BABYLON.MeshBuilder.CreateBox(
      `${name}_rw`,
      { width: 1.55, height: 0.55, depth: 0.04 },
      scene
    );
    rearWindow.position = new BABYLON.Vector3(0, 1.18, -1.05);
    rearWindow.rotation.x = 0.35;
    rearWindow.parent = root;
    rearWindow.material = glassMat;

    // Side windows
    [-1, 1].forEach((side) => {
      const sideWin = BABYLON.MeshBuilder.CreateBox(
        `${name}_sw_${side}`,
        { width: 0.04, height: 0.45, depth: 1.5 },
        scene
      );
      sideWin.position = new BABYLON.Vector3(side * 0.83, 1.22, -0.1);
      sideWin.parent = root;
      sideWin.material = glassMat;
    });

    // Side mirrors
    [-1, 1].forEach((side) => {
      const mirror = BABYLON.MeshBuilder.CreateBox(
        `${name}_mirror_${side}`,
        { width: 0.18, height: 0.12, depth: 0.18 },
        scene
      );
      mirror.position = new BABYLON.Vector3(side * 1.05, 1.0, 0.55);
      mirror.parent = root;
      mirror.material = bodyMat;
    });

    // Front bumper
    const frontBumper = BABYLON.MeshBuilder.CreateBox(
      `${name}_fb`,
      { width: 1.95, height: 0.25, depth: 0.25 },
      scene
    );
    frontBumper.position = new BABYLON.Vector3(0, 0.32, 2.15);
    frontBumper.parent = root;
    frontBumper.material = trimMat;

    // Rear bumper
    const rearBumper = BABYLON.MeshBuilder.CreateBox(
      `${name}_rb`,
      { width: 1.95, height: 0.25, depth: 0.25 },
      scene
    );
    rearBumper.position = new BABYLON.Vector3(0, 0.32, -2.15);
    rearBumper.parent = root;
    rearBumper.material = trimMat;

    // Front grille (chrome-ish)
    const grille = BABYLON.MeshBuilder.CreateBox(
      `${name}_grille`,
      { width: 1.1, height: 0.18, depth: 0.06 },
      scene
    );
    grille.position = new BABYLON.Vector3(0, 0.5, 2.22);
    grille.parent = root;
    grille.material = trimMat;

    // Door creases (thin dark lines) — visual detail
    [-1, 1].forEach((side) => {
      const crease = BABYLON.MeshBuilder.CreateBox(
        `${name}_crease_${side}`,
        { width: 0.02, height: 0.5, depth: 1.6 },
        scene
      );
      crease.position = new BABYLON.Vector3(side * 0.96, 0.72, -0.1);
      crease.parent = root;
      crease.material = trimMat;
    });

    // Wheel arches (small rounded boxes above each wheel)
    const wheelPositions = [
      { x: -0.95, z: 1.4 },
      { x: 0.95, z: 1.4 },
      { x: -0.95, z: -1.4 },
      { x: 0.95, z: -1.4 }
    ];

    const tireMat = new BABYLON.StandardMaterial(`${name}_tireMat`, scene);
    tireMat.diffuseColor = new BABYLON.Color3(0.04, 0.04, 0.05);
    tireMat.specularColor = new BABYLON.Color3(0.1, 0.1, 0.1);

    const rimMat = new BABYLON.StandardMaterial(`${name}_rimMat`, scene);
    rimMat.diffuseColor = new BABYLON.Color3(0.55, 0.55, 0.6);
    rimMat.specularColor = new BABYLON.Color3(0.95, 0.95, 0.95);
    rimMat.specularPower = 128;
    rimMat.emissiveColor = new BABYLON.Color3(0.06, 0.06, 0.07);

    wheelPositions.forEach((p, idx) => {
      // Tire (slightly thicker)
      const tire = BABYLON.MeshBuilder.CreateCylinder(
        `${name}_tire${idx}`,
        { height: 0.32, diameter: 0.72, tessellation: 18 },
        scene
      );
      tire.rotation.z = Math.PI / 2;
      tire.position = new BABYLON.Vector3(p.x, 0.36, p.z);
      tire.material = tireMat;
      tire.parent = root;

      // Rim (smaller cylinder inside)
      const rim = BABYLON.MeshBuilder.CreateCylinder(
        `${name}_rim${idx}`,
        { height: 0.34, diameter: 0.42, tessellation: 12 },
        scene
      );
      rim.rotation.z = Math.PI / 2;
      rim.position = new BABYLON.Vector3(p.x, 0.36, p.z);
      rim.material = rimMat;
      rim.parent = root;
    });

    if (!isPlayer) {
      // Tail lights for traffic cars (red glowing)
      const tailMat = new BABYLON.StandardMaterial(`${name}_tailMat`, scene);
      tailMat.emissiveColor = new BABYLON.Color3(1, 0.08, 0.05);
      tailMat.diffuseColor = new BABYLON.Color3(0.6, 0.05, 0.03);
      [-0.65, 0.65].forEach((x, i) => {
        const tail = BABYLON.MeshBuilder.CreateBox(
          `${name}_tail${i}`,
          { width: 0.45, height: 0.15, depth: 0.08 },
          scene
        );
        tail.position = new BABYLON.Vector3(x, 0.62, -2.22);
        tail.material = tailMat;
        tail.parent = root;
      });

      // Front headlights for traffic cars (white emissive)
      const frontMat = new BABYLON.StandardMaterial(`${name}_frontMat`, scene);
      frontMat.emissiveColor = new BABYLON.Color3(1, 0.97, 0.85);
      frontMat.diffuseColor = new BABYLON.Color3(1, 1, 0.95);
      [-0.65, 0.65].forEach((x, i) => {
        const front = BABYLON.MeshBuilder.CreateBox(
          `${name}_front${i}`,
          { width: 0.4, height: 0.16, depth: 0.08 },
          scene
        );
        front.position = new BABYLON.Vector3(x, 0.58, 2.22);
        front.material = frontMat;
        front.parent = root;
      });

      // License plate
      const plate = BABYLON.MeshBuilder.CreateBox(
        `${name}_plate`,
        { width: 0.7, height: 0.18, depth: 0.04 },
        scene
      );
      plate.position = new BABYLON.Vector3(0, 0.45, -2.25);
      const plateMat = new BABYLON.StandardMaterial(`${name}_plateMat`, scene);
      plateMat.diffuseColor = new BABYLON.Color3(0.95, 0.95, 0.9);
      plateMat.emissiveColor = new BABYLON.Color3(0.3, 0.3, 0.28);
      plate.material = plateMat;
      plate.parent = root;
    } else {
      // Player: emissive front headlight panels (visible from outside but mostly invisible from cockpit)
      const headlightMat = new BABYLON.StandardMaterial(`${name}_hlMat`, scene);
      headlightMat.emissiveColor = new BABYLON.Color3(1, 0.97, 0.85);
      [-0.65, 0.65].forEach((x, i) => {
        const hl = BABYLON.MeshBuilder.CreateBox(
          `${name}_hl${i}`,
          { width: 0.4, height: 0.16, depth: 0.08 },
          scene
        );
        hl.position = new BABYLON.Vector3(x, 0.58, 2.22);
        hl.material = headlightMat;
        hl.parent = root;
      });
    }

    return root;
  };

  const createCar = (scene) => {
    const car = buildCarMesh(scene, 'player', new BABYLON.Color3(0.2, 0.35, 0.85), true);
    car.position = new BABYLON.Vector3(0, 0, 0);
    // First-person cockpit view: hide the player's own car body so we don't see
    // the blue chassis through the windshield. The cockpit PNG overlay handles
    // the visual interior. Headlights stay parented to the car for lighting.
    car.getChildMeshes().forEach((m) => m.setEnabled(false));

    // Headlights (actual lights illuminating the road)
    const leftHeadlight = new BABYLON.SpotLight(
      'leftHeadlight',
      new BABYLON.Vector3(-0.7, 0.55, 2.1),
      new BABYLON.Vector3(0, -0.15, 1),
      Math.PI / 3,
      2,
      scene
    );
    leftHeadlight.intensity = 2.5;
    leftHeadlight.diffuse = new BABYLON.Color3(1, 0.95, 0.85);
    leftHeadlight.parent = car;

    const rightHeadlight = new BABYLON.SpotLight(
      'rightHeadlight',
      new BABYLON.Vector3(0.7, 0.55, 2.1),
      new BABYLON.Vector3(0, -0.15, 1),
      Math.PI / 3,
      2,
      scene
    );
    rightHeadlight.intensity = 2.5;
    rightHeadlight.diffuse = new BABYLON.Color3(1, 0.95, 0.85);
    rightHeadlight.parent = car;

    return car;
  };

  const createTraffic = (scene) => {
    const state = gameStateRef.current;
    const trafficCars = [];
    // 4 fixed lanes (2 per direction) so cars never share the same X
    const LANES = [-7, -3.5, 3.5, 7];

    // Spawn one car every ~120m ahead of the player. 11 vehicles (20% more
    // than the previous 9-slot rotation — user request) cover ~1320m of road
    // and keep a steady, dense, varied stream of approaching/overtaking
    // traffic without overlapping in any single lane.
    for (let i = 0; i < 11; i++) {
      const placeholder = new BABYLON.TransformNode(`traffic${i}_root`, scene);
      const laneIdx = i % LANES.length;
      placeholder.position = new BABYLON.Vector3(
        LANES[laneIdx],
        0,
        40 + i * 120
      );

      placeholder.metadata = {
        speed: 18 + Math.random() * 12,
        laneIdx,
        halfLen: 2.6,
        // Lane-change state machine
        // phase: 'cruise' → ('indicate' → 'change') → 'cruise'
        phase: 'cruise',
        phaseEnd: 4 + Math.random() * 8, // first decision after 4-12s
        targetLane: laneIdx
      };
      trafficCars.push(placeholder);
    }
    state.traffic = trafficCars;

    // Async GLB load — low-poly vehicle mini pack (12 vehicles: 4 muscle + 4 SUV + 4 truck)
    // Vehicle categories distributed across slots — when all GLBs load, each
    // slot gets a clone from its category bucket so cars/SUVs/trucks all show.
    const categoryOrder = ['suv', 'car', 'truck', 'suv', 'truck', 'car', 'suv', 'car', 'truck', 'car', 'suv'];
    trafficCars.forEach((placeholder, i) => {
      const category = categoryOrder[i % categoryOrder.length];
      placeholder.metadata.category = category;
      placeholder.metadata.isTruck = category === 'truck';
      if (placeholder.metadata.isTruck) {
        placeholder.metadata.speed = 12 + Math.random() * 6; // 12-18 m/s
        placeholder.metadata.halfLen = 3.5;
      }
    });

    // Load all 5 GLBs in parallel as AssetContainers, then spawn one per slot.
    // AssetContainer + instantiateModelsToScene() is the recommended Babylon
    // approach for cloning glTF hierarchies — the container holds the geometry
    // off-scene, and each instantiate creates a fresh hierarchy with its own
    // meshes (NOT shared InstancedMesh references which caused visibility
    // problems with our previous approach).
    Promise.all(
      VEHICLE_GLBS.map((vDef) =>
        BABYLON.SceneLoader.LoadAssetContainerAsync('', vDef.url, scene)
          .then((container) => {
            // Compute bbox from the assets WITHOUT adding them to the scene.
            // Add briefly, measure, then remove.
            container.addAllToScene();
            const tpl = container.meshes[0];
            const bb = tpl.getHierarchyBoundingVectors(true);
            const dx = bb.max.x - bb.min.x;
            const dz = bb.max.z - bb.min.z;
            const length = Math.max(dx, dz);
            container.removeAllFromScene();
            container.materials.forEach((m) => {
              if (m && typeof m.maxSimultaneousLights !== 'undefined') {
                m.maxSimultaneousLights = 4;
              }
            });
            if (length < 0.01) return null;
            const needsRotateY = dx > dz;
            const scale = vDef.targetLen / length;
            return { container, category: vDef.category, scale, minY: bb.min.y, needsRotateY, url: vDef.url, rotYExtra: vDef.rotYExtra || 0, rotXExtra: vDef.rotXExtra || 0, skipAutoRotateY: !!vDef.skipAutoRotateY };
          })
          .catch((err) => {
            console.warn('[VEHICLE GLB] load FAILED:', vDef.url, err);
            return null;
          })
      )
    ).then((results) => {
      const templates = results.filter(Boolean);
      if (templates.length === 0) {
        console.warn('[VEHICLE GLB] No vehicle GLBs loaded successfully');
        return;
      }
      const byCategory = {
        car: templates.filter((v) => v.category === 'car'),
        suv: templates.filter((v) => v.category === 'suv'),
        truck: templates.filter((v) => v.category === 'truck')
      };

      trafficCars.forEach((placeholder, i) => {
        const cat = placeholder.metadata.category;
        const pool = (byCategory[cat] && byCategory[cat].length > 0)
          ? byCategory[cat]
          : templates;
        const pick = pool[Math.floor(Math.random() * pool.length)];

        // instantiateModelsToScene() creates fully independent mesh hierarchies
        // (NOT InstancedMesh objects). Each spawn gets its own real Meshes,
        // which avoids the InstancedMesh visibility/culling problems that
        // plagued our earlier `instantiateHierarchy()` approach.
        const entries = pick.container.instantiateModelsToScene(
          (name) => `traffic${i}_${pick.category}_${name}`,
          false // doNotInstantiate=false → produce real Mesh clones
        );
        const inst = entries.rootNodes && entries.rootNodes[0];
        if (!inst) {
          console.warn(`[VEHICLE GLB] instantiateModelsToScene failed for slot ${i} (${pick.url})`);
          return;
        }
        // Make sure the new hierarchy is enabled & visible
        inst.setEnabled(true);
        inst.getDescendants().forEach((d) => {
          if (d.setEnabled) d.setEnabled(true);
          if ('isVisible' in d) d.isVisible = true;
          // Brighten material so the car's painted colors are visible at night.
          if (d.material && !d.material._driveSoberBoosted) {
            const mat = d.material;
            const boost = (c) => Math.min(1, c * 4 + 0.05);
            if (mat.albedoTexture || mat.albedoColor) {
              if (mat.albedoTexture) {
                mat.emissiveTexture = mat.albedoTexture;
                mat.emissiveColor = new BABYLON.Color3(1, 1, 1);
                // Reduced from 1.0 → 0.55 so light-colored / white paint jobs
                // don't blow out into a glowing white blob at night. The
                // hemi + directional moonlight fill still keeps darker cars
                // visible.
                mat.emissiveIntensity = 0.55;
              } else {
                const a = mat.albedoColor || new BABYLON.Color3(0.6, 0.6, 0.6);
                mat.emissiveColor = new BABYLON.Color3(boost(a.r), boost(a.g), boost(a.b));
              }
              if (mat.ambientColor) mat.ambientColor = new BABYLON.Color3(0.7, 0.7, 0.7);
            } else if (mat.diffuseTexture || mat.diffuseColor) {
              if (mat.diffuseTexture) {
                mat.emissiveTexture = mat.diffuseTexture;
                mat.emissiveColor = new BABYLON.Color3(1, 1, 1);
              } else {
                const a = mat.diffuseColor || new BABYLON.Color3(0.6, 0.6, 0.6);
                mat.emissiveColor = new BABYLON.Color3(boost(a.r), boost(a.g), boost(a.b));
              }
            }
            mat._driveSoberBoosted = true;
          }
        });

        // Wrap the instance in a TransformNode we control for scale/rotation.
        // This keeps the glTF loader's __root__ scaling (1, 1, -1) untouched
        // (handedness conversion).
        const carBody = new BABYLON.TransformNode(`carBody_${i}`, scene);
        carBody.parent = placeholder;
        carBody.scaling = new BABYLON.Vector3(pick.scale, pick.scale, pick.scale);
        const yRot = pick.skipAutoRotateY
          ? pick.rotYExtra
          : ((pick.needsRotateY ? Math.PI / 2 : 0) + pick.rotYExtra);
        carBody.rotationQuaternion = BABYLON.Quaternion.FromEulerAngles(
          pick.rotXExtra || 0, yRot, 0
        );
        inst.parent = carBody;
        inst.position = new BABYLON.Vector3(0, 0, 0);
        // Clear any inst rotation so carBody is the sole orientation source.
        inst.rotation = new BABYLON.Vector3(0, 0, 0);
        inst.rotationQuaternion = null;
        inst.computeWorldMatrix(true);
        carBody.computeWorldMatrix(true);
        const wbb = inst.getHierarchyBoundingVectors(true);
        carBody.position.y = placeholder.position.y - wbb.min.y;
      });
    });
  };

  const createSteeringWheel = (scene, camera) => {
    // Cockpit interior is now rendered via the PNG HTML overlay.
    // We keep this function as a no-op so the rest of the pipeline (speedometer,
    // steering wheel rotation, radio screen color) silently skips its visual updates.
    return;
    // eslint-disable-next-line no-unreachable
    const state = gameStateRef.current;

    // ======= COCKPIT INTERIOR (matches Forza-style cockpit reference) =======
    // All meshes parented to camera so they stay locked to the driver's POV.

    const interiorMat = new BABYLON.StandardMaterial('interiorMat', scene);
    interiorMat.diffuseColor = new BABYLON.Color3(0.04, 0.04, 0.05);
    interiorMat.specularColor = new BABYLON.Color3(0.12, 0.12, 0.14);
    interiorMat.specularPower = 32;
    interiorMat.emissiveColor = new BABYLON.Color3(0.012, 0.012, 0.015);

    const interiorTrimMat = new BABYLON.StandardMaterial('interiorTrimMat', scene);
    interiorTrimMat.diffuseColor = new BABYLON.Color3(0.08, 0.08, 0.09);
    interiorTrimMat.specularColor = new BABYLON.Color3(0.25, 0.25, 0.28);
    interiorTrimMat.emissiveColor = new BABYLON.Color3(0.02, 0.02, 0.025);

    // A-pillars — angled roof posts on left/right sides of windshield
    [-1, 1].forEach((side) => {
      const aPillar = BABYLON.MeshBuilder.CreateBox(
        `cockpit_aPillar_${side}`,
        { width: 0.16, height: 1.2, depth: 0.18 },
        scene
      );
      aPillar.position = new BABYLON.Vector3(side * 0.85, 0.18, 1.0);
      aPillar.rotation.x = -0.35;
      aPillar.rotation.z = side * 0.08;
      aPillar.parent = camera;
      aPillar.material = interiorMat;
      aPillar.isPickable = false;
    });

    // Roof overhang (subtle dark band at top of view)
    const roof = BABYLON.MeshBuilder.CreateBox(
      'cockpit_roof',
      { width: 2.4, height: 0.25, depth: 1.2 },
      scene
    );
    roof.position = new BABYLON.Vector3(0, 0.75, 0.55);
    roof.rotation.x = -0.18;
    roof.parent = camera;
    roof.material = interiorMat;
    roof.isPickable = false;

    // Rear-view mirror housing (hangs from top center)
    const mirrorStem = BABYLON.MeshBuilder.CreateBox(
      'mirrorStem',
      { width: 0.04, height: 0.12, depth: 0.04 },
      scene
    );
    mirrorStem.position = new BABYLON.Vector3(0.05, 0.62, 0.95);
    mirrorStem.parent = camera;
    mirrorStem.material = interiorMat;
    mirrorStem.isPickable = false;

    const mirror = BABYLON.MeshBuilder.CreateBox(
      'rearMirror',
      { width: 0.5, height: 0.16, depth: 0.07 },
      scene
    );
    mirror.position = new BABYLON.Vector3(0.05, 0.5, 0.95);
    mirror.rotation.x = 0.05;
    mirror.parent = camera;
    const mirrorMat = new BABYLON.StandardMaterial('mirrorMat', scene);
    mirrorMat.diffuseColor = new BABYLON.Color3(0.02, 0.03, 0.05);
    mirrorMat.specularColor = new BABYLON.Color3(0.6, 0.6, 0.7);
    mirrorMat.specularPower = 256;
    mirrorMat.emissiveColor = new BABYLON.Color3(0.04, 0.04, 0.06);
    mirror.material = mirrorMat;
    mirror.isPickable = false;

    // Side window frames / B-pillars (vertical strips on far edges)
    [-1, 1].forEach((side) => {
      const sideFrame = BABYLON.MeshBuilder.CreateBox(
        `cockpit_side_${side}`,
        { width: 0.5, height: 1.4, depth: 0.5 },
        scene
      );
      sideFrame.position = new BABYLON.Vector3(side * 1.15, 0.1, 0.55);
      sideFrame.rotation.y = side * -0.15;
      sideFrame.parent = camera;
      sideFrame.material = interiorMat;
      sideFrame.isPickable = false;
    });

    // Lower door panels (visible bottom-left/right corners)
    [-1, 1].forEach((side) => {
      const doorPanel = BABYLON.MeshBuilder.CreateBox(
        `cockpit_door_${side}`,
        { width: 0.45, height: 0.7, depth: 1.5 },
        scene
      );
      doorPanel.position = new BABYLON.Vector3(side * 1.05, -0.45, 0.45);
      doorPanel.parent = camera;
      doorPanel.material = interiorTrimMat;
      doorPanel.isPickable = false;
    });

    // Dashboard panel — main horizontal cowl below the windshield
    const dash = BABYLON.MeshBuilder.CreateBox(
      'dashboard',
      { width: 2.6, height: 0.55, depth: 0.8 },
      scene
    );
    dash.position = new BABYLON.Vector3(0, -0.55, 1.0);
    dash.rotation.x = 0.15;
    dash.material = interiorTrimMat;
    dash.parent = camera;
    dash.isPickable = false;

    // Dashboard top lip (the angled hood you see in Forza shots)
    const dashTop = BABYLON.MeshBuilder.CreateBox(
      'dashboardTop',
      { width: 2.6, height: 0.08, depth: 0.6 },
      scene
    );
    dashTop.position = new BABYLON.Vector3(0, -0.3, 0.85);
    dashTop.rotation.x = 0.25;
    dashTop.material = interiorMat;
    dashTop.parent = camera;
    dashTop.isPickable = false;

    // Steering wheel parent — sit closer to camera and angled for Forza cockpit feel
    const wheelRoot = new BABYLON.TransformNode('wheelRoot', scene);
    wheelRoot.parent = camera;
    wheelRoot.position = new BABYLON.Vector3(0, -0.62, 0.78);
    wheelRoot.rotation = new BABYLON.Vector3(-0.7, 0, 0); // strong tilt toward driver

    // Steering wheel ring (torus) — larger for better cockpit feel
    const ring = BABYLON.MeshBuilder.CreateTorus(
      'wheelRing',
      { diameter: 0.7, thickness: 0.06, tessellation: 36 },
      scene
    );
    ring.rotation.x = Math.PI / 2;
    const wheelMat = new BABYLON.StandardMaterial('wheelMat', scene);
    wheelMat.diffuseColor = new BABYLON.Color3(0.04, 0.04, 0.05);
    wheelMat.specularColor = new BABYLON.Color3(0.55, 0.55, 0.6);
    wheelMat.specularPower = 96;
    wheelMat.emissiveColor = new BABYLON.Color3(0.025, 0.025, 0.03);
    ring.material = wheelMat;
    ring.parent = wheelRoot;

    // Spokes
    const spokeMat = new BABYLON.StandardMaterial('spokeMat', scene);
    spokeMat.diffuseColor = new BABYLON.Color3(0.08, 0.08, 0.09);
    spokeMat.emissiveColor = new BABYLON.Color3(0.02, 0.02, 0.025);

    [0, Math.PI * 2 / 3, Math.PI * 4 / 3].forEach((angle, idx) => {
      const spoke = BABYLON.MeshBuilder.CreateBox(
        `spoke${idx}`,
        { width: 0.05, height: 0.025, depth: 0.32 },
        scene
      );
      spoke.rotation.y = angle;
      spoke.material = spokeMat;
      spoke.parent = wheelRoot;
    });

    // Center hub
    const hub = BABYLON.MeshBuilder.CreateCylinder(
      'wheelHub',
      { height: 0.08, diameter: 0.2, tessellation: 16 },
      scene
    );
    hub.rotation.x = Math.PI / 2;
    const hubMat = new BABYLON.StandardMaterial('hubMat', scene);
    hubMat.diffuseColor = new BABYLON.Color3(0.12, 0.12, 0.14);
    hubMat.emissiveColor = new BABYLON.Color3(0.06, 0.06, 0.07);
    hubMat.specularColor = new BABYLON.Color3(0.4, 0.4, 0.45);
    hub.material = hubMat;
    hub.parent = wheelRoot;

    // Steering column
    const column = BABYLON.MeshBuilder.CreateCylinder(
      'column',
      { height: 0.5, diameter: 0.08 },
      scene
    );
    column.position = new BABYLON.Vector3(0, -0.2, 0.15);
    column.rotation.x = 0.5;
    const colMat = new BABYLON.StandardMaterial('colMat', scene);
    colMat.diffuseColor = new BABYLON.Color3(0.05, 0.05, 0.06);
    column.material = colMat;
    column.parent = camera;

    state.steeringWheel = wheelRoot;

    // Speedometer dial cluster mounted on dashboard
    const dialRoot = new BABYLON.TransformNode('dialRoot', scene);
    dialRoot.parent = camera;
    dialRoot.position = new BABYLON.Vector3(-0.45, -0.45, 1.05);
    dialRoot.rotation = new BABYLON.Vector3(-0.45, 0, 0); // tilt face toward driver

    // Dial face (slightly recessed disk)
    const dialFace = BABYLON.MeshBuilder.CreateCylinder(
      'dialFace',
      { height: 0.02, diameter: 0.28, tessellation: 32 },
      scene
    );
    dialFace.rotation.x = Math.PI / 2;
    const dialFaceMat = new BABYLON.StandardMaterial('dialFaceMat', scene);
    dialFaceMat.diffuseColor = new BABYLON.Color3(0.03, 0.04, 0.05);
    dialFaceMat.emissiveColor = new BABYLON.Color3(0.08, 0.1, 0.13);
    dialFaceMat.specularColor = new BABYLON.Color3(0.2, 0.2, 0.2);
    dialFace.material = dialFaceMat;
    dialFace.parent = dialRoot;

    // Dial bezel (outer ring)
    const bezel = BABYLON.MeshBuilder.CreateTorus(
      'dialBezel',
      { diameter: 0.3, thickness: 0.025, tessellation: 32 },
      scene
    );
    bezel.rotation.x = Math.PI / 2;
    const bezelMat = new BABYLON.StandardMaterial('bezelMat', scene);
    bezelMat.diffuseColor = new BABYLON.Color3(0.18, 0.18, 0.2);
    bezelMat.specularColor = new BABYLON.Color3(0.7, 0.7, 0.7);
    bezelMat.emissiveColor = new BABYLON.Color3(0.03, 0.03, 0.04);
    bezel.material = bezelMat;
    bezel.parent = dialRoot;

    // Tick marks around the dial (every 30 degrees, full arc 240 degrees)
    const tickMat = new BABYLON.StandardMaterial('tickMat', scene);
    tickMat.emissiveColor = new BABYLON.Color3(0.85, 0.88, 0.95);
    tickMat.diffuseColor = new BABYLON.Color3(0.9, 0.9, 0.95);
    for (let t = 0; t <= 9; t++) {
      const tickAngle = (t / 9) * (Math.PI * 1.5) - Math.PI * 0.75 + Math.PI; // sweep from -135 to +135 (around bottom)
      const isMajor = t % 3 === 0;
      const tick = BABYLON.MeshBuilder.CreateBox(
        `tick${t}`,
        {
          width: isMajor ? 0.015 : 0.008,
          height: 0.005,
          depth: isMajor ? 0.03 : 0.018
        },
        scene
      );
      const r = 0.115;
      tick.position = new BABYLON.Vector3(
        Math.sin(tickAngle) * r,
        0.012,
        -Math.cos(tickAngle) * r
      );
      tick.rotation.y = tickAngle;
      tick.material = tickMat;
      tick.parent = dialRoot;
    }

    // Needle pivot
    const needlePivot = new BABYLON.TransformNode('needlePivot', scene);
    needlePivot.position = new BABYLON.Vector3(0, 0.015, 0);
    needlePivot.parent = dialRoot;

    // Needle (thin red arrow)
    const needle = BABYLON.MeshBuilder.CreateBox(
      'speedoNeedle',
      { width: 0.008, height: 0.006, depth: 0.11 },
      scene
    );
    needle.position = new BABYLON.Vector3(0, 0, -0.045); // pivots around dial center
    const needleMat = new BABYLON.StandardMaterial('needleMat', scene);
    needleMat.diffuseColor = new BABYLON.Color3(1, 0.18, 0.15);
    needleMat.emissiveColor = new BABYLON.Color3(0.95, 0.15, 0.12);
    needle.material = needleMat;
    needle.parent = needlePivot;

    // Center cap
    const cap = BABYLON.MeshBuilder.CreateCylinder(
      'needleCap',
      { height: 0.015, diameter: 0.025, tessellation: 16 },
      scene
    );
    cap.rotation.x = Math.PI / 2;
    cap.material = bezelMat;
    cap.parent = dialRoot;

    state.speedoNeedle = needlePivot;

    // ======= CENTER CONSOLE RADIO / INFOTAINMENT SCREEN =======
    // Mounted to the right side of the dashboard (matches Forza reference)
    const radioRoot = new BABYLON.TransformNode('radioRoot', scene);
    radioRoot.parent = camera;
    radioRoot.position = new BABYLON.Vector3(0.42, -0.5, 1.02);
    radioRoot.rotation = new BABYLON.Vector3(-0.45, 0, 0);

    // Bezel
    const radioBezel = BABYLON.MeshBuilder.CreateBox(
      'radioBezel',
      { width: 0.32, height: 0.22, depth: 0.04 },
      scene
    );
    const radioBezelMat = new BABYLON.StandardMaterial('radioBezelMat', scene);
    radioBezelMat.diffuseColor = new BABYLON.Color3(0.06, 0.06, 0.07);
    radioBezelMat.emissiveColor = new BABYLON.Color3(0.02, 0.02, 0.025);
    radioBezel.material = radioBezelMat;
    radioBezel.parent = radioRoot;

    // Screen (emissive)
    const radioScreen = BABYLON.MeshBuilder.CreateBox(
      'radioScreen',
      { width: 0.28, height: 0.18, depth: 0.005 },
      scene
    );
    radioScreen.position.z = -0.025;
    const radioScreenMat = new BABYLON.StandardMaterial('radioScreenMat', scene);
    // Start: off (dark dim)
    radioScreenMat.diffuseColor = new BABYLON.Color3(0.05, 0.06, 0.08);
    radioScreenMat.emissiveColor = new BABYLON.Color3(0.04, 0.05, 0.07);
    radioScreenMat.disableLighting = true;
    radioScreen.material = radioScreenMat;
    radioScreen.parent = radioRoot;

    // Three round station buttons below the screen
    const btnMat = new BABYLON.StandardMaterial('radioBtnMat', scene);
    btnMat.diffuseColor = new BABYLON.Color3(0.1, 0.1, 0.12);
    btnMat.emissiveColor = new BABYLON.Color3(0.04, 0.04, 0.05);
    [-0.09, 0, 0.09].forEach((x, i) => {
      const btn = BABYLON.MeshBuilder.CreateCylinder(
        `radioBtn${i}`,
        { height: 0.015, diameter: 0.04, tessellation: 12 },
        scene
      );
      btn.position = new BABYLON.Vector3(x, -0.14, -0.02);
      btn.rotation.x = Math.PI / 2;
      btn.material = btnMat;
      btn.parent = radioRoot;
    });

    state.radio.screen = radioScreenMat;
  };

  const setRadioStation = (station) => {
    const state = gameStateRef.current;
    const audio = state.audio;
    const radio = state.radio;
    // Binary: 0 = off, anything else = on (only one track)
    const isOn = station > 0;
    radio.station = isOn ? 1 : 0;
    radio.on = isOn;

    // Update screen color
    if (radio.screen) {
      radio.screen.emissiveColor = isOn
        ? new BABYLON.Color3(0.15, 0.45, 0.85)  // glowing blue when on
        : new BABYLON.Color3(0.04, 0.05, 0.07); // dim when off
    }

    // Update label overlay
    if (radio.screenLabel) {
      radio.screenLabel.textContent = isOn ? 'RADIO  ON' : 'RADIO  OFF';
      radio.screenLabel.style.color = isOn ? 'rgba(255,255,255,0.9)' : '#445566';
    }

    // Fade the radio gain in/out (audio track keeps looping in the background)
    if (!audio || !audio.radio) return;
    audio.radio.gain.gain.setTargetAtTime(
      isOn ? 0.32 : 0,
      audio.ctx.currentTime,
      0.15
    );
  };

  const ENGINE_AUDIO_URL =
    'https://customer-assets.emergentagent.com/job_intoxicated-drive/artifacts/mblijgyk_freesound_community-inside-car-noise-while-driving-29978.mp3';

  const RADIO_TRACK_URL =
    'https://customer-assets.emergentagent.com/job_intoxicated-drive/artifacts/n1p1ecy5_jingle%20rc%20%282%29.mp3';

  const setupAudio = (drinks) => {
    const state = gameStateRef.current;
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();

      // ======= ENGINE AUDIO (user-supplied MP3, looped) =======
      // Lowpass filter gets more aggressive with drinks → muffled hearing
      const lowpass = ctx.createBiquadFilter();
      lowpass.type = 'lowpass';
      lowpass.frequency.value = Math.max(450, 3500 - drinks * 510);
      lowpass.Q.value = 1;

      // Engine gain (ramps up once playback starts)
      const engineGain = ctx.createGain();
      engineGain.gain.value = 0.0;

      // Master gain
      const master = ctx.createGain();
      master.gain.value = 0.55;

      // Routing: engineSource → engineGain → lowpass → master → out
      engineGain.connect(lowpass);
      lowpass.connect(master);
      master.connect(ctx.destination);

      // Source placeholder — created once the buffer is loaded
      let engineSource = null;

      // Safari <14.5 implements decodeAudioData with a callback only (returns
      // undefined). Wrap it so both forms resolve to a Promise.
      const decode = (buf) =>
        new Promise((resolve, reject) => {
          try {
            const maybePromise = ctx.decodeAudioData(
              buf,
              (data) => resolve(data),
              (err) => reject(err)
            );
            if (maybePromise && typeof maybePromise.then === 'function') {
              maybePromise.then(resolve, reject);
            }
          } catch (e) {
            reject(e);
          }
        });

      // Fetch and decode the MP3
      fetch(ENGINE_AUDIO_URL)
        .then((r) => r.arrayBuffer())
        .then((buf) => decode(buf))
        .then((decoded) => {
          engineSource = ctx.createBufferSource();
          engineSource.buffer = decoded;
          engineSource.loop = true;
          engineSource.playbackRate.value = 0.9;
          engineSource.connect(engineGain);
          engineSource.start();
          // Expose for runtime updates
          if (state.audio) state.audio.engineSource = engineSource;
        })
        .catch((err) => console.warn('Engine audio load failed', err));

      // ======= RADIO MUSIC CHAIN (looped MP3 track) =======
      const radioGain = ctx.createGain();
      radioGain.gain.value = 0.32; // audible by default — radio starts ON

      const radioFilter = ctx.createBiquadFilter();
      radioFilter.type = 'lowpass';
      radioFilter.frequency.value = Math.max(800, 4000 - drinks * 350);
      radioFilter.Q.value = 0.7;

      radioFilter.connect(radioGain);
      radioGain.connect(master);

      // Source placeholder — created once the MP3 buffer decodes
      let radioSource = null;

      fetch(RADIO_TRACK_URL)
        .then((r) => r.arrayBuffer())
        .then((buf) => decode(buf))
        .then((decoded) => {
          radioSource = ctx.createBufferSource();
          radioSource.buffer = decoded;
          radioSource.loop = true; // loop forever when track ends
          radioSource.connect(radioFilter);
          radioSource.start();
          if (state.audio && state.audio.radio) state.audio.radio.source = radioSource;
        })
        .catch((err) => console.warn('Radio track load failed', err));

      state.audio = {
        ctx,
        engineSource,
        engineGain,
        lowpass,
        master,
        drinks,
        radio: {
          source: radioSource,
          gain: radioGain,
          filter: radioFilter
        }
      };

      // Radio starts ON by default — press R to toggle
      state.radio.station = 1;
      state.radio.on = true;

      // Resume context on first interaction (browser autoplay policy)
      const resumeOnce = () => {
        if (ctx.state === 'suspended') ctx.resume();
        window.removeEventListener('keydown', resumeOnce);
        window.removeEventListener('pointerdown', resumeOnce);
      };
      window.addEventListener('keydown', resumeOnce, { once: true });
      window.addEventListener('pointerdown', resumeOnce, { once: true });
    } catch (e) {
      console.warn('Audio setup failed', e);
    }
  };

  const updateAudio = (deltaTime) => {
    const state = gameStateRef.current;
    const audio = state.audio;
    if (!audio) return;

    // Slight pitch shift mapped to speed: 0 km/h → 0.85x, 120 km/h → 1.25x
    if (audio.engineSource) {
      const speedRatio = Math.min(1.5, state.speed / 120);
      const targetRate = 0.85 + speedRatio * 0.4;
      audio.engineSource.playbackRate.setTargetAtTime(
        targetRate,
        audio.ctx.currentTime,
        0.15
      );
    }

    // Volume ramps up once driving begins; stays audible at idle
    const targetGain = Math.min(0.85, 0.35 + state.speed * 0.004);
    audio.engineGain.gain.setTargetAtTime(targetGain, audio.ctx.currentTime, 0.12);
  };

  const setupRainEffect = (scene, camera) => {
    // Subtle windshield rain droplets — always active
    BABYLON.Effect.ShadersStore['rainFragmentShader'] = `
      precision highp float;
      varying vec2 vUV;
      uniform sampler2D textureSampler;
      uniform float time;

      float hash21(vec2 p) {
        p = fract(p * vec2(123.34, 456.21));
        p += dot(p, p + 45.32);
        return fract(p.x * p.y);
      }

      void main(void) {
        vec2 uv = vUV;
        vec3 color = texture2D(textureSampler, uv).rgb;

        // Grid of droplet cells
        vec2 gridSize = vec2(14.0, 22.0);
        vec2 grid = uv * gridSize;
        vec2 gridId = floor(grid);
        vec2 gridUV = fract(grid) - 0.5;

        float seed = hash21(gridId);
        float speed = 0.05 + seed * 0.12;
        float trailY = fract(time * speed + seed * 9.7);

        // Droplet position drifts down
        vec2 dropCenter = vec2(
          (seed - 0.5) * 0.3,
          (0.5 - trailY) * 0.85
        );
        float dropSize = 0.12 + seed * 0.08;
        float dist = length((gridUV - dropCenter) * vec2(1.0, 1.4));

        // Static stuck droplets (less motion) on top of moving ones
        float staticDrop = smoothstep(0.05, 0.0, length(gridUV - vec2(seed - 0.5, (hash21(gridId + 7.0) - 0.5))));

        if (dist < dropSize) {
          float strength = 1.0 - smoothstep(0.0, dropSize, dist);
          // Refraction: sample slightly offset
          vec2 refractOff = (gridUV - dropCenter) * 0.025 * strength;
          vec3 refracted = texture2D(textureSampler, uv - refractOff).rgb;
          // Brighten edge for surface tension highlight
          float edge = pow(strength, 6.0) * 0.35;
          color = mix(color, refracted * 1.05, strength * 0.65) + vec3(edge);
        }

        // Sparse stuck droplets
        if (staticDrop > 0.0) {
          vec2 stOff = (gridUV - vec2(seed - 0.5, hash21(gridId + 7.0) - 0.5)) * 0.018;
          vec3 stuck = texture2D(textureSampler, uv - stOff).rgb;
          color = mix(color, stuck * 1.04 + vec3(0.06), staticDrop * 0.55);
        }

        gl_FragColor = vec4(color, 1.0);
      }
    `;

    const rainPP = new BABYLON.PostProcess(
      'rain',
      'rain',
      ['time'],
      null,
      1.0,
      camera
    );
    rainPP.onApply = (effect) => {
      effect.setFloat('time', performance.now() / 1000);
    };
  };

  const setupImpairmentEffects = (scene, camera, drinks) => {
    if (drinks === 0) return;

    // Progressive motion blur — kicks in at 1 drink, ramps gently
    const motionBlur = new BABYLON.MotionBlurPostProcess(
      'motionBlur',
      scene,
      1.0,
      camera
    );
    // Strength curve: drinks 1→0.12, 2→0.22, 3→0.32, 4→0.45, 5→0.62, 6→0.85
    const strengthCurve = [0, 0.12, 0.22, 0.32, 0.45, 0.62, 0.85];
    motionBlur.motionStrength = strengthCurve[Math.min(6, drinks)];
    motionBlur.motionBlurSamples = 16;

    // Create custom post-process for chromatic aberration and blur
    if (drinks >= 1) {
      // Register shader BEFORE creating the PostProcess
      BABYLON.Effect.ShadersStore['impairmentFragmentShader'] = `
        precision highp float;
        varying vec2 vUV;
        uniform sampler2D textureSampler;
        uniform float drinkLevel;
        uniform float time;

        void main(void) {
          vec2 uv = vUV;
          
          // Chromatic aberration
          float aberration = drinkLevel * 0.003;
          vec2 direction = uv - vec2(0.5);
          vec2 uvR = uv + direction * aberration;
          vec2 uvB = uv - direction * aberration;
          
          float r = texture2D(textureSampler, uvR).r;
          float g = texture2D(textureSampler, uv).g;
          float b = texture2D(textureSampler, uvB).b;
          
          vec3 color = vec3(r, g, b);
          
          // Blur/double vision at high levels
          if (drinkLevel >= 5.0) {
            vec2 offset = vec2(sin(time * 0.5) * 0.01, cos(time * 0.7) * 0.01);
            vec3 color2 = texture2D(textureSampler, uv + offset).rgb;
            color = mix(color, color2, 0.4);
          }
          
          // Vignette/tunnel vision
          float dist = length(uv - vec2(0.5));
          float vignette = smoothstep(0.8, 0.3 - drinkLevel * 0.08, dist);
          color *= vignette;
          
          // Desaturation
          float gray = dot(color, vec3(0.299, 0.587, 0.114));
          color = mix(vec3(gray), color, 1.0 - drinkLevel * 0.1);
          
          gl_FragColor = vec4(color, 1.0);
        }
      `;

      const postProcess = new BABYLON.PostProcess(
        'impairment',
        'impairment',
        ['drinkLevel', 'time'],
        null,
        1.0,
        camera
      );

      postProcess.onApply = (effect) => {
        effect.setFloat('drinkLevel', drinks);
        effect.setFloat('time', performance.now() / 1000);
      };
    }
  };

  const setupInputHandlers = (drinks) => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') gameStateRef.current.actualInputs.left = true;
      if (e.key === 'ArrowRight') gameStateRef.current.actualInputs.right = true;
      if (e.key === 'ArrowUp') gameStateRef.current.actualInputs.up = true;
      if (e.key === 'ArrowDown') gameStateRef.current.actualInputs.down = true;
      // Radio controls
      if (e.key === 'r' || e.key === 'R') {
        const cur = gameStateRef.current.radio.station;
        setRadioStation(cur === 0 ? 1 : 0);
      }
    };

    const handleKeyUp = (e) => {
      if (e.key === 'ArrowLeft') gameStateRef.current.actualInputs.left = false;
      if (e.key === 'ArrowRight') gameStateRef.current.actualInputs.right = false;
      if (e.key === 'ArrowUp') gameStateRef.current.actualInputs.up = false;
      if (e.key === 'ArrowDown') gameStateRef.current.actualInputs.down = false;
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  };

  const updateCarPhysics = (car, camera, deltaTime, drinks) => {
    const state = gameStateRef.current;
    
    // Input smoothing — gets noticeably laggier as drinks rise.
    // 0 drinks: snappy (28). 3 drinks: sluggish (~16). 6 drinks: very laggy (~6).
    const steerAlpha = 1 - Math.exp(-Math.max(5, 28 - drinks * 3.7) * deltaTime);
    const pedalAlpha = 1 - Math.exp(-Math.max(4, 18 - drinks * 2.3) * deltaTime);

    // Apply input with delay
    state.inputBuffer.left =
      state.inputBuffer.left +
      ((state.actualInputs.left ? 1 : 0) - state.inputBuffer.left) * steerAlpha;
    state.inputBuffer.right =
      state.inputBuffer.right +
      ((state.actualInputs.right ? 1 : 0) - state.inputBuffer.right) * steerAlpha;
    state.inputBuffer.up =
      state.inputBuffer.up +
      ((state.actualInputs.up ? 1 : 0) - state.inputBuffer.up) * pedalAlpha;
    state.inputBuffer.down =
      state.inputBuffer.down +
      ((state.actualInputs.down ? 1 : 0) - state.inputBuffer.down) * pedalAlpha;

    // Speed model:
    //   - Cruise (no input): 50 + drinks*4 km/h. Drunk drivers drift faster
    //     at baseline (foot heavier on the pedal as inhibitions drop).
    //   - Hold ↑ to accelerate up to topSpeed.
    //   - topSpeed = 100 km/h sober → 75 km/h at 6 drinks (impaired pedal
    //     control means a drunk driver can't actually push the car as fast).
    const cruiseSpeed = 50 + drinks * 4;
    const topSpeed = Math.max(cruiseSpeed + 5, 100 - drinks * (25 / 6));
    const acceleration = 55 + drinks * 2;
    const deceleration = Math.max(15, 35 - drinks * 3);

    if (state.inputBuffer.up > 0.1) {
      state.speed = Math.min(topSpeed, state.speed + acceleration * deltaTime * state.inputBuffer.up);
    } else if (state.inputBuffer.down > 0.1) {
      state.speed = Math.max(0, state.speed - deceleration * deltaTime * state.inputBuffer.down);
    } else {
      // Cruise — gentle pull toward 50; stronger pull when drunk (loss of pedal control)
      state.speed = BABYLON.Scalar.Lerp(state.speed, cruiseSpeed, (0.5 + drinks * 0.1) * deltaTime);
    }
    // Final safety clamp — speed must stay in a sane range regardless of any
    // weird deltaTime spikes from tab pauses or hot-reloads.
    if (!Number.isFinite(state.speed) || state.speed < 0) state.speed = 0;
    if (state.speed > topSpeed * 1.05) state.speed = topSpeed;

    // ===== Responsive yet smooth two-stage steering =====
    // Stage 1: steering wheel angle tracks input. As drinks rise the response
    // grows sluggish and the wheel "fights back" with sway/drift.
    // Stage 2: car heading rotates from (wheel angle × speed factor).

    // Max steering wheel deflection — narrower range when drunk (heavy arms feel)
    const MAX_WHEEL_RAD = Math.max(0.7, 1.25 - drinks * 0.09);
    const targetWheelAngle = (state.inputBuffer.right - state.inputBuffer.left) * MAX_WHEEL_RAD;

    // Wheel turn rate: sober=14 (snappy), 6 drinks=3 (~4.5× slower)
    const wheelLerpRate = Math.max(2.5, 14.0 - drinks * 1.9);
    const wheelAlpha = 1 - Math.exp(-wheelLerpRate * deltaTime);
    state.wheelAngle = state.wheelAngle + (targetWheelAngle - state.wheelAngle) * wheelAlpha;

    // Steering sway scales smoothly with EVERY drink (1 = barely noticeable,
    // 6 = brutal). At 6 drinks the combined drift + oversteer + random twitches
    // make staying centered in a lane a real challenge.
    let autoSteerOffset = 0;
    if (drinks >= 1) {
      // 0.16 at 1 drink → 1.0 at 6 drinks (quadratic ramp for steeper feel)
      const impairFactor = Math.pow(drinks / 6, 1.4);
      // Slow body-roll drift — two sinusoids combined for a non-periodic feel.
      const drift =
        (Math.sin(Date.now() / 1300) * 0.5 + Math.cos(Date.now() / 2100) * 0.3) *
        0.18 *
        impairFactor;
      // Faster sway that resembles overcorrecting a wandering car.
      const oversteer =
        (Math.sin(Date.now() / 480) + Math.cos(Date.now() / 770) * 0.6) *
        0.09 *
        impairFactor;
      // Random twitches — micro-jerks from impaired motor control.
      // Start subtle at 2 drinks, severe at 6.
      const twitchAmp =
        drinks >= 2 ? 0.04 + 0.05 * Math.max(0, (drinks - 2) / 4) : 0;
      const twitch = (Math.random() - 0.5) * twitchAmp;
      autoSteerOffset = drift + oversteer + twitch;
    }

    const effectiveWheelAngle = state.wheelAngle + autoSteerOffset;

    // Stage 2: car heading turns based on wheel angle.
    // Strong base turn rate even at low speed; scales gently with speed.
    // 30% + 10% additional responsiveness boost.
    const speedFactor = 0.129 + state.speed * 0.00086;
    const headingDelta = effectiveWheelAngle * speedFactor * deltaTime;
    state.carRotation += headingDelta;

    // Self-centering when no input is held (sober only)
    if (Math.abs(state.inputBuffer.right - state.inputBuffer.left) < 0.05 && drinks === 0) {
      const centerAlpha = 1 - Math.exp(-1.0 * deltaTime);
      state.carRotation = state.carRotation + (0 - state.carRotation) * centerAlpha;
    }

    car.rotation.y = state.carRotation;

    // Visually rotate the steering wheel in the cockpit (dramatic for feedback)
    if (state.steeringWheel) {
      const targetVisualRot = state.wheelAngle * 1.5;
      const visualAlpha = 1 - Math.exp(-15.0 * deltaTime);
      state.steeringInput =
        state.steeringInput + (targetVisualRot - state.steeringInput) * visualAlpha;
      state.steeringWheel.rotation.z = -state.steeringInput;
    }

    // Update position
    const moveVector = new BABYLON.Vector3(
      Math.sin(state.carRotation) * state.speed * deltaTime,
      0,
      Math.cos(state.carRotation) * state.speed * deltaTime
    );

    car.position.addInPlace(moveVector);
    state.carPosition.x = car.position.x;
    state.carPosition.z = car.position.z;

    // Apply road elevation to the car
    const roadY = getRoadY(car.position.z);
    car.position.y = roadY;

    // No clamping — vehicle CAN crash into side barriers

    // Update camera to follow car from inside the cockpit
    camera.position.x = car.position.x;
    camera.position.y = roadY + 1.35;
    camera.position.z = car.position.z;
    const lookTarget = new BABYLON.Vector3(
      car.position.x + Math.sin(state.carRotation) * 15,
      roadY + 1.0,
      car.position.z + Math.cos(state.carRotation) * 15
    );
    camera.setTarget(lookTarget);

    // Update distance
    state.distance += state.speed * deltaTime;

    // Rotate speedometer needle (sweeps 240° from 0-160 km/h)
    if (state.speedoNeedle) {
      const speedRatio = Math.min(1, state.speed / 160);
      // -135° (left) to +135° (right) around Y axis of dial face
      const targetAngle = -Math.PI * 0.75 + speedRatio * Math.PI * 1.5;
      // Smooth needle motion
      const currentAngle = state.speedoNeedle.rotation.y;
      state.speedoNeedle.rotation.y = BABYLON.Scalar.Lerp(
        currentAngle,
        targetAngle,
        Math.min(1, deltaTime * 5)
      );
    }
  };

  const updateCameraEffects = (camera, deltaTime, drinks) => {
    // No camera sway until 3+ drinks, then very subtle and progressive
    if (drinks < 3) return;

    const state = gameStateRef.current;
    const impairFactor = (drinks - 2) / 4; // 0.25 at d=3 → 1.0 at d=6
    const swayAmount = 0.0009 * impairFactor; // much subtler than before
    const swaySpeed = 1.6 + impairFactor * 1.4;

    state.cameraSwayOffset += swaySpeed * deltaTime;
    camera.rotation.z = Math.sin(state.cameraSwayOffset) * swayAmount;
    camera.rotation.x += Math.cos(state.cameraSwayOffset * 0.7) * swayAmount * 0.4;
  };

  const updateTraffic = (scene, deltaTime) => {
    const state = gameStateRef.current;
    const playerZ = state.carPosition.z;
    const LANES = [-7, -3.5, 3.5, 7];
    const MIN_SAME_LANE_GAP = 14;

    const laneClear = (laneIdx, z, ignoreCar) => {
      for (const other of state.traffic) {
        if (other === ignoreCar) continue;
        if (other.metadata.laneIdx !== laneIdx) continue;
        if (Math.abs(other.position.z - z) < MIN_SAME_LANE_GAP) return false;
      }
      return true;
    };

    state.traffic.forEach((trafficCar) => {
      const md = trafficCar.metadata;
      const distAhead = trafficCar.position.z - playerZ;

      // ---- Lane-change state machine ----
      // Cars decide to switch lanes while cruising, then slide into the target
      // lane once the player closes to ~250 m (giving time to manoeuvre).
      md.phaseEnd -= deltaTime;
      if (md.phase === 'cruise' && md.phaseEnd <= 0) {
        const onLeftHalf = md.laneIdx < 2;
        const desiredLane = onLeftHalf ? md.laneIdx + 1 : md.laneIdx - 1;
        const canMove =
          desiredLane >= 0 &&
          desiredLane < LANES.length &&
          laneClear(desiredLane, trafficCar.position.z, trafficCar);
        if (canMove && Math.random() < 0.55) {
          md.targetLane = desiredLane;
          md.phase = 'indicate';
          // Max time it'll wait for the player before giving up and changing
          // anyway (so cars far ahead of the player still complete their move).
          md.phaseEnd = 12.0;
        } else {
          md.phaseEnd = 5 + Math.random() * 7;
        }
      } else if (md.phase === 'indicate') {
        // Start the lane change as soon as the car comes into the player's
        // view (within ~250 m ahead) — gives the player plenty of time to
        // manoeuvre around the cut-in.
        const triggerByDistance = distAhead > 0 && distAhead <= 250;
        const triggerByTimeout = md.phaseEnd <= 0;
        if (triggerByDistance || triggerByTimeout) {
          md.phase = 'change';
          // 5 s slide — gentle, predictable cut-in.
          md.phaseEnd = 5.0;
        } else if (distAhead < -20) {
          // Player already passed — drop the lane change.
          md.phase = 'cruise';
          md.phaseEnd = 5 + Math.random() * 7;
        }
      } else if (md.phase === 'change' && md.phaseEnd <= 0) {
        // Lane change complete
        md.laneIdx = md.targetLane;
        md.phase = 'cruise';
        md.phaseEnd = 6 + Math.random() * 8;
      }

      // Slide X toward target lane during 'change' phase. Slide rate is
      // calibrated so a full 3.5 m lane gap covers in ~5 s (matches the
      // 5 s phaseEnd) — smooth, predictable, easy for the player to read.
      const targetX = LANES[md.phase === 'change' ? md.targetLane : md.laneIdx];
      const slideRate = md.phase === 'change' ? 0.7 : 0.8;
      trafficCar.position.x = BABYLON.Scalar.Lerp(
        trafficCar.position.x,
        targetX,
        Math.min(1, deltaTime * slideRate)
      );

      // ---- Forward motion with car-ahead awareness ----
      let speed = md.speed;
      for (const other of state.traffic) {
        if (other === trafficCar) continue;
        if (other.metadata.laneIdx !== md.laneIdx) continue;
        const dz = other.position.z - trafficCar.position.z;
        if (dz > 0 && dz < MIN_SAME_LANE_GAP) {
          speed = Math.min(speed, other.metadata.speed * 0.95);
        }
      }
      trafficCar.position.z += speed * deltaTime;
      trafficCar.position.y = getRoadY(trafficCar.position.z);

      // Recycle: when too far behind, respawn 120m beyond the furthest car
      // (so spacing stays ~120m between cars on the road ahead).
      if (trafficCar.position.z < playerZ - 60) {
        // Furthest car ahead = max Z among all other cars
        let furthestZ = playerZ;
        for (const o of state.traffic) {
          if (o === trafficCar) continue;
          if (o.position.z > furthestZ) furthestZ = o.position.z;
        }
        let placed = false;
        for (let attempt = 0; attempt < 8; attempt++) {
          const newLane = Math.floor(Math.random() * LANES.length);
          const newZ = furthestZ + 120 + Math.random() * 30;
          if (laneClear(newLane, newZ, trafficCar)) {
            md.laneIdx = newLane;
            md.targetLane = newLane;
            md.phase = 'cruise';
            md.phaseEnd = 5 + Math.random() * 8;
            trafficCar.position.x = LANES[newLane];
            trafficCar.position.z = newZ;
            md.speed = 18 + Math.random() * 12;
            placed = true;
            break;
          }
        }
        if (!placed) {
          trafficCar.position.z = furthestZ + 140;
        }
      }
    });
  };

  const checkCollisions = (car, scene) => {
    const state = gameStateRef.current;

    // Check traffic collisions
    for (const trafficCar of state.traffic) {
      const dx = car.position.x - trafficCar.position.x;
      const dz = car.position.z - trafficCar.position.z;
      const distance = Math.sqrt(dx * dx + dz * dz);
      if (distance < 2.8) {
        handleCrash();
        return;
      }
    }

    // Side barrier collisions — barriers sit at x = ±11. Car half-width ≈ 1.
    // Trigger crash when the car's edge contacts the barrier line.
    if (Math.abs(car.position.x) > 9.8) {
      handleCrash();
      return;
    }
  };

  const handleCrash = () => {
    const state = gameStateRef.current;
    if (state.crashed) return;

    state.crashed = true;

    // Play crash sound effect
    if (crashAudioRef.current) {
      try {
        crashAudioRef.current.currentTime = 0;
        const playPromise = crashAudioRef.current.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {});
        }
      } catch (e) {
        // ignore
      }
    }

    // Duck the engine audio so the crash sound is prominent
    const audio = state.audio;
    if (audio && audio.engineGain) {
      try {
        audio.engineGain.gain.setTargetAtTime(0.05, audio.ctx.currentTime, 0.05);
      } catch (e) {
        // ignore
      }
    }

    const timeSurvived = (Date.now() - state.startTime) / 1000;

    onCrash({
      timeSurvived: timeSurvived,
      distanceTraveled: state.distance,
      currentSpeed: state.speed
    });
  };

  const updateStats = () => {
    const state = gameStateRef.current;
    const timeSurvived = (Date.now() - state.startTime) / 1000;

    onUpdateStats({
      timeSurvived: timeSurvived,
      distanceTraveled: state.distance,
      currentSpeed: state.speed
    });
  };

  return (
    <>
      <canvas
        ref={canvasRef}
        style={{
          width: '100%',
          height: '100%',
          display: 'block',
          position: 'absolute',
          top: 0,
          left: 0
        }}
        data-testid="game-canvas"
      />

      {/* Cockpit PNG overlay — stretched to fill the game frame edge-to-edge. */}
      <img
        src={COCKPIT_IMG_URL}
        alt=""
        data-testid="cockpit-overlay"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'fill',
          zIndex: 25,
          pointerEvents: 'none',
          userSelect: 'none'
        }}
        draggable={false}
      />

      {/* Radio toggle — click or press R */}
      <div
        data-testid="radio-label"
        onClick={() => {
          const cur = gameStateRef.current.radio.station;
          setRadioStation(cur === 0 ? 1 : 0);
        }}
        style={{
          position: 'absolute',
          top: '24px',
          right: '24px',
          padding: '10px 16px',
          background: 'rgba(0, 0, 0, 0.65)',
          backdropFilter: 'blur(8px)',
          border: '1px solid rgba(255, 255, 255, 0.18)',
          color: '#445566',
          fontFamily: 'JetBrains Mono, monospace',
          fontSize: '12px',
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          zIndex: 30,
          cursor: 'pointer',
          userSelect: 'none'
        }}
      >
        <div style={{ opacity: 0.5, fontSize: '10px', marginBottom: '4px' }}>
          [R] / click to toggle
        </div>
        <div ref={radioLabelRef} data-testid="radio-station-label">
          RADIO  ON
        </div>
      </div>
    </>
  );
};

export default GameScene;
